import React, { useState, useRef, useEffect, Fragment } from "react";
import Select from 'react-select';
import { motion } from "framer-motion";
import { BsBoxSeam, BsGraphUp } from "react-icons/bs";
import { AiOutlineUser, AiOutlineShoppingCart } from "react-icons/ai";
import { CiMoneyBill } from "react-icons/ci";
import NavigateBack from "../NavigateBack";
import StatusChangeModal from "../FunctionalComponents/StatusChangeModal";
import TransferOrderModal from "../FunctionalComponents/TransferOrderModal";
import ReturnRestoreModal from "../FunctionalComponents/ReturnRestoreModal";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { FiDownload, FiPrinter, FiShare2, FiCheck, FiMapPin, FiMessageCircle } from "react-icons/fi";
import { FaWhatsapp, FaExchangeAlt, FaExclamationTriangle, FaUndoAlt, FaTruck } from "react-icons/fa";
import { useParams, useNavigate, useSearchParams } from "react-router-dom"; // Import useParams and useNavigate
import { getOrders, changeOrderStatus, cancelOrderItem, updateItemStatus, fetchTemplatePreferences, getOrderTimeline, flagException, resolveException, addOrderNote, getAvailableDrivers, assignDriverToOrder, getMarketplaceOrders, updateMarketplaceOrderStatus } from "../../services/ClientService"; // Import getOrders, changeOrderStatus, cancelOrderItem, updateItemStatus and fetchTemplatePreferences API
import { formatDateToLong } from "../../utils/dateUtils";
import { generateReceipt } from "../../utils/thermalTemplates";
import { toast } from 'react-toastify';
import ConfirmModal from '../ConfirmModal';
import FreeCashBillingLine from './FreeCashBillingLine';
import { calculateGrandTotalFromItemTotal, getReceiptDiscounts } from '../../utils/orderBillingHelpers';
import { adaptMarketplaceOrderForOverview, MARKETPLACE_STATUS_FLOW } from '../../utils/marketplaceOrderAdapter';

const STATUS_FLOW = [
    "Received",
    "Confirmed",
    "Shipped",
    "Out For Delivery",
    "Delivered",
    "Cancelled"
];

/** Flatten statusHistory for a compact horizontal timeline (newest last). */
const buildStatusTimelineEntries = (orderDetails) => {
    if (!orderDetails) return [];
    const hist = orderDetails.statusHistory;
    if (!Array.isArray(hist) || hist.length === 0) {
        const at = orderDetails.orderDate || orderDetails.orderPlacedOn;
        const label = orderDetails.status || "Received";
        return at ? [{ label, at }] : [{ label, at: null }];
    }
    const rows = hist
        .map((h) => ({
            label: h.statusTo || h.status || "—",
            at: h.changedOn || h.timestamp || h.at,
        }))
        .filter((r) => r.at);
    rows.sort((a, b) => new Date(a.at) - new Date(b.at));
    return rows;
};

/** Parse DD/MM/YYYY expiry string to Date */
const parseExpiryDate = (expiryStr) => {
    if (!expiryStr || typeof expiryStr !== 'string' || expiryStr.length !== 10) return null;
    const parts = expiryStr.split('/');
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
    if (!dd || !mm || !yyyy) return null;
    const d = new Date(yyyy, mm - 1, dd);
    d.setHours(23, 59, 59, 999);
    return isNaN(d.getTime()) ? null : d;
};

const getOrderDeliveredDate = (orderDetails) => {
    if (!orderDetails) return null;
    const hist = orderDetails.statusHistory;
    if (Array.isArray(hist)) {
        const delivered = [...hist]
            .reverse()
            .find((h) => (h.statusTo || h.status) === 'Delivered');
        if (delivered?.changedOn || delivered?.timestamp) {
            return new Date(delivered.changedOn || delivered.timestamp);
        }
    }
    if (orderDetails.status === 'Delivered') {
        return new Date(orderDetails.orderDate || orderDetails.orderPlacedOn || Date.now());
    }
    return null;
};

/** Show expiry when delivered < 30 days ago or product is expired */
const getItemExpiryDisplay = (orderDetails, item) => {
    const expiryStr =
        item.expiryDate ||
        item.selectedWeight?.expiryDate ||
        (item.selectedWeight && typeof item.selectedWeight === 'object' && !item.selectedWeight.weight
            ? item.selectedWeight.expiryDate
            : null);
    if (!expiryStr) return null;

    const expiryDate = parseExpiryDate(expiryStr);
    if (!expiryDate) return null;

    const now = new Date();
    const isExpired = expiryDate < now;

    if (isExpired) {
        return { text: `Expiry: ${expiryStr}`, colorClass: 'text-red-600 font-semibold' };
    }

    const deliveredAt = getOrderDeliveredDate(orderDetails);
    if (deliveredAt) {
        const daysSinceDelivery = (now.getTime() - deliveredAt.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceDelivery < 30) {
            return { text: `Expiry: ${expiryStr}`, colorClass: 'text-yellow-600 font-semibold' };
        }
    }

    return null;
};

const InvoiceTemplate = React.forwardRef(({ order, includeGstin = true }, ref) => {
    const itemsPerPage = 9;
    
    // Split items into pages
    const pages = [];
    for (let i = 0; i < order.items.length; i += itemsPerPage) {
        pages.push(order.items.slice(i, i + itemsPerPage));
    }

    // Calculate item totals based on GST type
    const itemTotals = order.items.map((item, idx) => {
        const gstType = order.razorpayItems[idx]?.gstType;
        const gstAmount = order.razorpayItems[idx]?.gstAmount || 0;
        const price = item.individualPrice * item.quantity;
        if (gstType === "exclusive") {
            return price + gstAmount;
        } else {
            return price;
        }
    });

    // Sum of all item totals
    const itemTotal = itemTotals.reduce((sum, val) => sum + val, 0);
    const { deliveryCharges, couponDiscount, freeCashDiscount } = getReceiptDiscounts(order);
    const grandTotal = calculateGrandTotalFromItemTotal(itemTotal, order);
    
    // Check if GST Invoice details should be shown
    const showGstInvoice = includeGstin && order.gstInvoice?.requested;
    
    // Seller/Branch state - dynamically from order data
    // Normalize to lowercase for comparison
    const SELLER_STATE = (order.branchState || '').toLowerCase().trim();
    
    // Helper function to extract state from delivery address (fallback)
    const extractStateFromAddress = (address) => {
        if (!address) return null;
        
        // Common Indian states list for matching
        const indianStates = [
            'andhra pradesh', 'arunachal pradesh', 'assam', 'bihar', 'chhattisgarh',
            'goa', 'gujarat', 'haryana', 'himachal pradesh', 'jharkhand', 'karnataka',
            'kerala', 'madhya pradesh', 'maharashtra', 'manipur', 'meghalaya', 'mizoram',
            'nagaland', 'odisha', 'punjab', 'rajasthan', 'sikkim', 'tamil nadu',
            'telangana', 'tripura', 'uttar pradesh', 'uttarakhand', 'west bengal',
            'delhi', 'jammu and kashmir', 'ladakh', 'chandigarh', 'puducherry',
            'lakshadweep', 'andaman and nicobar islands', 'dadra and nagar haveli and daman and diu'
        ];
        
        const addressLower = address.toLowerCase();
        
        // Try to find state in address
        for (const state of indianStates) {
            if (addressLower.includes(state)) {
                return state;
            }
        }
        
        return null;
    };
    
    // Determine if this is an Inter-State (IGST) or Intra-State (CGST+SGST) transaction
    // Priority 1: If B2B GST Invoice requested, use the GST state from customer's GSTIN (NOT delivery address)
    // Priority 2: Otherwise, determine based on delivery state comparison with seller state
    const determineIsInterState = () => {
        // If B2B order with GST Invoice details, compare GST state with branch state
        // This is the PRIMARY method - customer's GST registration state determines tax type
        if (order.gstInvoice?.requested) {
            // Get the customer's GST registered state (from GSTIN)
            const gstState = (order.gstInvoice.state || '').toLowerCase().trim();
            
            // Log for debugging
            console.log('[Invoice GST] B2B Order - Using GST Invoice State:', {
                gstState: gstState,
                branchState: SELLER_STATE,
                storedGstType: order.gstInvoice.gstType,
                deliveryState: order.deliveryState // Just for reference
            });
            
            // If we have GST state, compare with branch state
            if (gstState && SELLER_STATE) {
                const isInterState = gstState !== SELLER_STATE;
                console.log(`[Invoice GST] B2B Result: ${isInterState ? 'INTER-STATE (IGST)' : 'INTRA-STATE (CGST+SGST)'}`);
                console.log(`[Invoice GST] Reason: GST State (${gstState}) ${isInterState ? '!=' : '=='} Branch State (${SELLER_STATE})`);
                return isInterState;
            }
            
            // Fallback to stored gstType if state comparison not possible
            if (order.gstInvoice.gstType) {
                console.log('[Invoice GST] Using stored gstType:', order.gstInvoice.gstType);
                return order.gstInvoice.gstType === 'IGST';
            }
        }
        
        // If no branch state available, default to CGST+SGST
        if (!SELLER_STATE) {
            console.log('[Invoice GST] No branch state available, defaulting to CGST+SGST');
            return false;
        }
        
        // For non-B2B orders: Use delivery state to determine tax type
        // First try to use the deliveryState field directly (most reliable)
        let deliveryState = (order.deliveryState || '').toLowerCase().trim();
        
        // Fallback: Extract state from delivery address string
        if (!deliveryState) {
            const deliveryAddress = order.deliveryAddress || order.customerAddress || '';
            deliveryState = extractStateFromAddress(deliveryAddress);
        }
        
        // Log for debugging
        console.log('[Invoice GST] Regular Order - Using Delivery State:', {
            branchState: SELLER_STATE,
            deliveryState: deliveryState,
            deliveryStateRaw: order.deliveryState,
            deliveryAddress: order.deliveryAddress
        });
        
        // If we can identify the delivery state, compare with seller state
        if (deliveryState) {
            const isInterState = deliveryState !== SELLER_STATE;
            console.log(`[Invoice GST] Regular Result: ${isInterState ? 'INTER-STATE (IGST)' : 'INTRA-STATE (CGST+SGST)'}`);
            return isInterState; // Different state = Inter-State (IGST)
        }
        
        // Default to Intra-State (CGST+SGST) if state cannot be determined
        console.log('[Invoice GST] Cannot determine delivery state, defaulting to CGST+SGST');
        return false;
    };
    
    const isInterState = determineIsInterState();

    return (
        <div ref={ref} className="bg-white p-4 max-w-4xl mx-auto">
            {pages.map((pageItems, pageIndex) => (
                <div
                    key={pageIndex}
                    className="page-break"
                >
                    {/* Invoice Header - Enhanced */}
                    <div className="text-center mb-6 border-b-4 border-blue-600 pb-4 bg-gradient-to-r from-blue-50 to-cyan-50 -mx-4 -mt-4 px-4 pt-4 rounded-t-lg">
                        <h1 className="text-3xl font-extrabold text-blue-700 pb-2">DPBN SUPER MARTS PRIVATE LIMITED</h1>
                        <p className="text-gray-600 pb-1 text-sm leading-relaxed">
                            23-9-26/1, Nirmala Nagar, Near Railway Gate, Sattenapalli<br />
                            Palnadu District, Andhra Pradesh – 522403
                        </p>
                        <p className="text-gray-600 text-sm">
                            <span className="font-semibold">GST IN:</span> 37AALCD5749H1Z7 | 
                            <span className="font-semibold"> Phone:</span> +91 9493921579 | 
                            <span className="font-semibold"> Email:</span> dbecart25@gmail.com
                        </p>
                    </div>

                    {/* Order Details */}
                    {pageIndex === 0 && (
                        <>
                            <div className="flex flex-col md:flex-row justify-between mb-4 gap-6">
                                <div className="md:w-full">
                                    <h2 className="text-lg font-semibold text-gray-800 mb-3 border-b-2 border-blue-500 pb-1 inline-block">Customer Details</h2>
                                    <div className="mt-2 space-y-1">
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Name:</span> {order.customerName}</p>
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Phone:</span> {order.customerPhone}</p>
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Email:</span> {order.customerEmail || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="md:w-[50%]">
                                    <h2 className="text-lg font-semibold text-gray-800 mb-3 border-b-2 border-blue-500 pb-1 inline-block">Order Information</h2>
                                    <div className="mt-2 space-y-1">
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Order ID:</span> {order.id}</p>
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Date:</span> {order.date}</p>
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Status:</span> <span className="text-green-800 text-xs font-semibold">{order.status}</span></p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Delivery Details Section */}
                            <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                                <h2 className="text-lg font-semibold text-gray-800 mb-3 border-b-2 border-blue-500 pb-1 inline-block">Delivery Details</h2>
                                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Name:</span> {order.deliveryName || order.customerName}</p>
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Mobile:</span> {order.deliveryPhone || order.customerPhone}</p>
                                    </div>
                                    <div>
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Delivery Type:</span> <span className=" text-blue-800 text-xs font-semibold">{order.deliveryType || 'Standard'}</span></p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <p className="text-gray-700 text-sm"><span className="font-medium">Delivery Address:</span> {order.deliveryAddress || order.customerAddress}</p>
                                        {order.deliveryDirections && (
                                            <p className="text-gray-600 text-xs mt-1 italic">📍 {order.deliveryDirections}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* GST Invoice Details Section - Only show when customer requested and checkbox is checked */}
                            {showGstInvoice && (
                                <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-300">
                                    <h2 className="text-lg font-semibold text-gray-800 mb-3 border-b-2 border-green-500 pb-1 inline-block">
                                        📋 Buyer GST Details (B2B Invoice)
                                    </h2>
                                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <p className="text-gray-700 text-sm">
                                                <span className="font-semibold">GSTIN:</span>{' '}
                                                <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-900 font-bold tracking-wide">
                                                    {order.gstInvoice.gstin}
                                                </span>
                                            </p>
                                            {order.gstInvoice.companyName && (
                                                <p className="text-gray-700 text-sm mt-1">
                                                    <span className="font-semibold">Company Name:</span> {order.gstInvoice.companyName}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-gray-700 text-sm">
                                                <span className="font-semibold">State:</span>{' '}
                                                <span className="font-mono text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded mr-1">
                                                    {order.gstInvoice.stateCode}
                                                </span>
                                                {order.gstInvoice.state}
                                            </p>
                                            <p className="text-gray-700 text-sm mt-1">
                                                <span className="font-semibold">Email:</span> {order.gstInvoice.email}
                                            </p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <p className="text-xs text-gray-600 bg-white/50 px-3 py-1.5 rounded-md inline-block">
                                                <span className="font-semibold">GST Type:</span>{' '}
                                                <span className={`font-semibold ${order.gstInvoice.gstType === 'IGST' ? 'text-orange-600' : 'text-blue-600'}`}>
                                                    {order.gstInvoice.gstType === 'IGST' 
                                                        ? 'Inter-State (IGST Applicable)' 
                                                        : 'Intra-State (CGST + SGST Applicable)'}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Items Table with GST details - Enhanced */}
                    <table className="w-full mb-4 border-collapse shadow-sm">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white text-[11px]">
                                <th className="text-left py-3 px-3 font-semibold rounded-tl-lg">Item</th>
                                <th className="text-center py-3 px-2 font-semibold">Weight</th>
                                <th className="text-center py-3 px-2 font-semibold">Qty</th>
                                <th className="text-right py-3 px-2 font-semibold">Price</th>
                                {/* Show IGST columns for Inter-State, CGST/SGST for Intra-State */}
                                {isInterState ? (
                                    <>
                                        <th className="text-center py-3 px-2 font-semibold">IGST%</th>
                                        <th className="text-center py-3 px-2 font-semibold">IGST Amt</th>
                                    </>
                                ) : (
                                    <>
                                        <th className="text-center py-3 px-2 font-semibold">CGST%</th>
                                        <th className="text-center py-3 px-2 font-semibold">SGST%</th>
                                        <th className="text-center py-3 px-2 font-semibold">CGST Amt</th>
                                        <th className="text-center py-3 px-2 font-semibold">SGST Amt</th>
                                    </>
                                )}
                                <th className="text-right py-3 px-3 font-semibold rounded-tr-lg">Total</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                            {pageItems.map((item, localIndex) => {
                                const globalIndex = pageIndex * itemsPerPage + localIndex;
                                const gstType = order.razorpayItems[globalIndex]?.gstType;
                                const gstAmount = order.razorpayItems[globalIndex]?.gstAmount || 0;
                                const cgstPercent = order.razorpayItems[globalIndex]?.cgstPercent || 0;
                                const sgstPercent = order.razorpayItems[globalIndex]?.sgstPercent || 0;
                                const cgstAmount = order.razorpayItems[globalIndex]?.cgstAmount || 0;
                                const sgstAmount = order.razorpayItems[globalIndex]?.sgstAmount || 0;
                                // For IGST: combine CGST+SGST percent and amounts
                                const igstPercent = cgstPercent + sgstPercent;
                                const igstAmount = cgstAmount + sgstAmount;
                                const price = item.individualPrice * item.quantity;
                                const total = gstType === "exclusive" ? price + gstAmount : price;
                                return (
                                    <tr key={globalIndex} className="border-b border-gray-200 hover:bg-blue-50 transition-colors text-[12px]">
                                        <td className="py-3 px-3 text-gray-800 align-top">
                                            <span className="font-semibold">{item.name}</span>
                                            {gstType && (
                                                <span className="block text-[10px] text-gray-500 mt-0.5">
                                                    GST: {gstType}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3 px-2 text-center text-gray-700 align-top">
                                            {typeof item.weight === 'object' && item.weight !== null
                                                ? item.weight.weightLabel || `${item.weight.weightValue} ${item.weight.metricLabel}`
                                                : item.weight || 'N/A'}
                                        </td>
                                        <td className="py-3 px-2 text-center text-gray-800 font-semibold align-top">{item.quantity}</td>
                                        <td className="py-3 px-2 text-right text-gray-800 align-top">
                                            ₹{item.individualPrice.toFixed(2)}
                                        </td>
                                        {/* Show IGST columns for Inter-State, CGST/SGST for Intra-State */}
                                        {isInterState ? (
                                            <>
                                                <td className="py-3 px-2 text-center text-gray-700 align-top">
                                                    {igstPercent}%
                                                </td>
                                                <td className="py-3 px-2 text-center text-gray-700 align-top">
                                                    ₹{igstAmount.toFixed(2)}
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="py-3 px-2 text-center text-gray-700 align-top">
                                                    {cgstPercent}%
                                                </td>
                                                <td className="py-3 px-2 text-center text-gray-700 align-top">
                                                    {sgstPercent}%
                                                </td>
                                                <td className="py-3 px-2 text-center text-gray-700 align-top">
                                                    ₹{cgstAmount.toFixed(2)}
                                                </td>
                                                <td className="py-3 px-2 text-center text-gray-700 align-top">
                                                    ₹{sgstAmount.toFixed(2)}
                                                </td>
                                            </>
                                        )}
                                        <td className="py-3 px-3 text-right text-gray-900 font-bold align-top">
                                            ₹{total.toFixed(2)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Enhanced Total Section with Better Styling */}
                    {pageIndex === pages.length - 1 && (
                        <div className="flex justify-end mb-6">
                            <div className="w-full md:w-1/2 bg-gradient-to-br from-gray-50 to-blue-50 p-5 rounded-xl shadow-lg border border-blue-200">
                                <div className="flex justify-between mb-2.5 text-gray-700">
                                    <span className="font-medium">
                                        Item Total ({order.items.length} items)
                                    </span>
                                    <span className="text-gray-900 font-semibold">₹{itemTotal.toFixed(2)}</span>
                                </div>

                                {/* Coupon Discount */}
                                {couponDiscount > 0 && (
                                    <div className="flex justify-between mb-2.5 text-gray-700">
                                        <span className="font-medium">Coupon Discount</span>
                                        <span className="text-green-600 font-semibold">-₹{couponDiscount.toFixed(2)}</span>
                                    </div>
                                )}
                                <FreeCashBillingLine amount={freeCashDiscount} />

                                <div className="flex justify-between mb-2.5 text-gray-700">
                                    <span className="font-medium">Delivery Charges</span>
                                    <span className="text-gray-900 font-semibold">₹{deliveryCharges.toFixed(2)}</span>
                                </div>

                                <hr className="border-t-2 border-dashed border-gray-400 my-3" />

                                <div className="flex justify-between text-xl font-bold">
                                    <span className="text-gray-900">Grand Total</span>
                                    <span className="text-blue-700">₹{grandTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Footer - Enhanced */}
                    {pageIndex === pages.length - 1 && (
                        <div className="border-t-2 border-blue-600 text-center text-sm text-gray-600 bg-gradient-to-r from-blue-50 to-cyan-50 -mx-4 -mb-4 px-4 pb-4 pt-3 rounded-b-lg">
                            <p className="font-semibold text-blue-700 mb-1">Thank you for your order! ❤️</p>
                            <p className="text-xs leading-relaxed">
                                <span className="font-medium">Terms & Conditions:</span> Payment must be completed before dispatch or at the time of delivery
                            </p>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
});

const BillTemplate = React.forwardRef(({ order, size }, ref) => {
    const sizeClasses = {
        "extra small": "text-xs p-1",
        small: "text-sm p-2",
        medium: "text-base p-3",
        large: "text-lg p-4",
    };

    const truncateName = (name) => {
        return name.length > 20 ? `${name.slice(0, 20)}...` : name;
    };

    // Calculate item totals based on GST type (same as Invoice)
    const itemsPerPage = 1000; // No pagination for bill
    const itemTotals = order.items.map((item, idx) => {
        const gstType = order.razorpayItems[idx]?.gstType;
        const gstAmount = order.razorpayItems[idx]?.gstAmount || 0;
        const price = item.individualPrice * item.quantity;
        if (gstType === "exclusive") {
            return price + gstAmount;
        } else {
            return price;
        }
    });

    const itemTotal = itemTotals.reduce((sum, val) => sum + val, 0);
    const { deliveryCharges, couponDiscount, freeCashDiscount } = getReceiptDiscounts(order);
    const grandTotal = calculateGrandTotalFromItemTotal(itemTotal, order);

    return (
        <div ref={ref} className={`bg-white ${sizeClasses[size]} max-w-md mx-auto border-2 border-gray-200 rounded-lg`}>
            <div className="text-center mb-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-t-lg">
                <h1 className="font-bold text-lg">DPBN SUPER MARTS</h1>
                <p className="text-xs mt-1">GST IN: 37AALCD5749H1Z7</p>
            </div>
            
            {/* Customer Section */}
            <div className="mb-3 px-2">
                <h2 className="font-semibold text-gray-800 text-sm border-b border-gray-300 pb-1 mb-2">Customer</h2>
                <div className="space-y-0.5">
                    <p className="text-gray-700 text-xs"><span className="font-medium">Name:</span> {order.customerName}</p>
                    <p className="text-gray-700 text-xs"><span className="font-medium">Phone:</span> {order.customerPhone}</p>
                </div>
            </div>
            
            {/* Delivery Section */}
            <div className="mb-3 px-2 bg-blue-50 py-2 rounded">
                <h2 className="font-semibold text-gray-800 text-sm border-b border-blue-300 pb-1 mb-2">Delivery Details</h2>
                <div className="space-y-0.5">
                    <p className="text-gray-700 text-xs"><span className="font-medium">Name:</span> {order.deliveryName || order.customerName}</p>
                    <p className="text-gray-700 text-xs"><span className="font-medium">Mobile:</span> {order.deliveryPhone || order.customerPhone}</p>
                    <p className="text-gray-700 text-xs"><span className="font-medium">Address:</span> {order.deliveryAddress || order.customerAddress}</p>
                    <p className="text-gray-700 text-xs"><span className="font-medium">Type:</span> <span className="inline-block px-1.5 py-0.5 bg-blue-200 text-blue-900 rounded text-[10px] font-semibold">{order.deliveryType || 'Standard'}</span></p>
                </div>
            </div>
            
            {/* Order Section */}
            <div className="mb-3 px-2">
                <h2 className="font-semibold text-gray-800 text-sm border-b border-gray-300 pb-1 mb-2">Order</h2>
                <div className="space-y-0.5">
                    <p className="text-gray-700 text-xs"><span className="font-medium">ID:</span> {order.id}</p>
                    <p className="text-gray-700 text-xs"><span className="font-medium">Date:</span> {order.date}</p>
                    <p className="text-gray-700 text-xs"><span className="font-medium">Status:</span> <span className="inline-block px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-[10px] font-semibold">{order.status}</span></p>
                </div>
            </div>
            <table className="w-full mb-2 border-collapse shadow-sm">
                <thead>
                    <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                        <th className="text-left py-2 px-2 font-semibold rounded-tl">Item</th>
                        <th className="text-center py-2 px-1 font-semibold">Wt</th>
                        <th className="text-center py-2 px-1 font-semibold">Qty</th>
                        <th className="text-right py-2 px-1 font-semibold">Price</th>
                        <th className="text-center py-2 px-1 font-semibold">GST</th>
                        <th className="text-right py-2 px-2 font-semibold rounded-tr">Total</th>
                    </tr>
                </thead>
                <tbody className="bg-white">
                    {order.items.map((item, index) => {
                        const gstType = order.razorpayItems[index]?.gstType;
                        const gstAmount = order.razorpayItems[index]?.gstAmount || 0;
                        const price = item.individualPrice * item.quantity;
                        const couponDiscount = order.couponDiscount || 0;
                        const total = gstType === "exclusive" ? price + gstAmount : price;
                        return (
                            <tr key={index} className="border-b border-gray-200 hover:bg-blue-50">
                                <td className="py-2 px-2 text-gray-800 font-medium">{truncateName(item.name)}</td>
                                <td className="py-2 px-1 text-center text-gray-700 text-xs">
                                    {typeof item.weight === 'object' && item.weight !== null
                                        ? item.weight.weightLabel || `${item.weight.weightValue} ${item.weight.metricLabel}`
                                        : item.weight || 'N/A'}
                                </td>
                                <td className="py-2 px-1 text-center text-gray-800 font-semibold">{item.quantity}</td>
                                <td className="py-2 px-1 text-right text-gray-800">₹{item.individualPrice.toFixed(2)}</td>
                                <td className="py-2 px-1 text-center text-gray-700 text-xs">₹{gstAmount.toFixed(2)}</td>
                                <td className="py-2 px-2 text-right text-gray-900 font-bold">₹{total.toFixed(2)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div className="mt-3 mb-2 px-2 py-3 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between text-gray-700 font-semibold mb-1.5 text-sm">
                    <span>Item Total ({order.items.length})</span>
                    <span className="text-gray-900">₹{itemTotal.toFixed(2)}</span>
                </div>
                {couponDiscount > 0 && (
                    <div className="flex justify-between text-gray-700 font-semibold mb-1.5 text-sm">
                        <span>Coupon Discount</span>
                        <span className="text-green-600">-₹{couponDiscount.toFixed(2)}</span>
                    </div>
                )}
                <FreeCashBillingLine
                    amount={freeCashDiscount}
                    className="flex justify-between text-gray-700 font-semibold mb-1.5 text-sm"
                    labelClass=""
                    valueClass="text-purple-600"
                />
                {deliveryCharges > 0 && (
                    <div className="flex justify-between text-gray-700 font-semibold mb-1.5 text-sm">
                        <span>Delivery</span>
                        <span className="text-gray-900">₹{deliveryCharges.toFixed(2)}</span>
                    </div>
                )}
                <div className="flex justify-between text-gray-900 font-bold border-t-2 border-dashed border-gray-400 pt-2 mt-2">
                    <span className="text-base">Grand Total</span>
                    <span className="text-blue-700 text-lg">₹{grandTotal.toFixed(2)}</span>
                </div>
            </div>
            
            {/* Bill Footer */}
            <div className="text-center text-xs text-gray-500 mt-3 pt-2 border-t border-gray-300">
                <p className="font-semibold">Thank you for your order! ❤️</p>
            </div>
        </div>
    );
});

const OrderOverview = () => {
    const componentRef = useRef();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isMarketplaceOrder = searchParams.get('source') === 'marketplace';
    const navigate = useNavigate();
    const [isGenerating, setIsGenerating] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);
    const billRef = useRef();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showGstDetails, setShowGstDetails] = useState(false);

    // Courier selection modal state (Ship Now)
    const [showCourierModal, setShowCourierModal] = useState(false);
    const [courierList, setCourierList] = useState([]);
    const [courierModalLoading, setCourierModalLoading] = useState(false);
    const [courierModalError, setCourierModalError] = useState('');
    const [courierOrderInfo, setCourierOrderInfo] = useState(null);
    const [shippingCourierId, setShippingCourierId] = useState(null); // currently shipping courier
    const [courierFilter, setCourierFilter] = useState('all'); // recommended | surface | air | all
    const [recommendedCourierId, setRecommendedCourierId] = useState(null);
    const [customerSelectedCourier, setCustomerSelectedCourier] = useState(null); // Courier chosen during checkout

    // Checkbox state for including item and bill details
    const [includeDetails, setIncludeDetails] = useState(true);

    // Checkbox state for including GSTIN details in invoice
    const [includeGstinInInvoice, setIncludeGstinInInvoice] = useState(true);

    // Transfer order state
    const [showTransferModal, setShowTransferModal] = useState(false);

    // Return and Restore (on behalf of customer, delivered orders)
    const [showReturnRestoreModal, setShowReturnRestoreModal] = useState(false);
    const [showReturnRequestModal, setShowReturnRequestModal] = useState(false);

    // Item cancellation state
    const [showCancelItemModal, setShowCancelItemModal] = useState(false);
    const [selectedCancelItem, setSelectedCancelItem] = useState(null);
    const [cancellingItem, setCancellingItem] = useState(false);
    const [itemCancellationReason, setItemCancellationReason] = useState('');

    // Vendor accordion state - track which items have expanded vendor info
    const [expandedVendorItems, setExpandedVendorItems] = useState({});

    // Toggle vendor accordion for a product item
    const toggleVendorAccordion = (itemIndex) => {
        setExpandedVendorItems(prev => ({
            ...prev,
            [itemIndex]: !prev[itemIndex]
        }));
    };

    // Individual item status change state
    const [showItemStatusModal, setShowItemStatusModal] = useState(false);
    const [selectedItemForStatus, setSelectedItemForStatus] = useState(null);
    const [updatingItemStatus, setUpdatingItemStatus] = useState(false);

    // Shiprocket cancel confirmation state
    const [showShiprocketCancelConfirm, setShowShiprocketCancelConfirm] = useState(false);

    // Post-Order Command Center state
    const [timeline, setTimeline] = useState([]);
    const [activeExceptions, setActiveExceptions] = useState([]);
    const [returnRequests, setReturnRequests] = useState([]);
    const [showExceptionModal, setShowExceptionModal] = useState(false);
    const [exceptionType, setExceptionType] = useState('');
    const [exceptionDescription, setExceptionDescription] = useState('');
    const [exceptionSeverity, setExceptionSeverity] = useState('medium');
    const [flaggingException, setFlaggingException] = useState(false);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [showTimelinePanel, setShowTimelinePanel] = useState(false);
    const [showResolveModal, setShowResolveModal] = useState(false);
    const [selectedExceptionToResolve, setSelectedExceptionToResolve] = useState(null);
    const [resolveNotes, setResolveNotes] = useState('');
    const [resolvingException, setResolvingException] = useState(false);

    // Template preferences from Template Manager
    const [templatePreferences, setTemplatePreferences] = useState({
        billSize: '80mm',
        templateId: 'classic'
    });

    useEffect(() => {
        // Disable scrolling on the root page
        document.body.style.overflow = "hidden";
        return () => {
            // Re-enable scrolling when the component unmounts
            document.body.style.overflow = "auto";
        };
    }, []);

    // Load template preferences from Template Manager settings
    useEffect(() => {
        const loadTemplatePreferences = async () => {
            try {
                const response = await fetchTemplatePreferences();
                if (response.success && response.data) {
                    setTemplatePreferences({
                        billSize: response.data.billSize || '80mm',
                        templateId: response.data.templateId || 'classic'
                    });
                }
            } catch (error) {
                console.error('Error loading template preferences:', error);
                // Keep default values if loading fails
            }
        };
        loadTemplatePreferences();
    }, []);

    useEffect(() => {
        fetchOrderDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isMarketplaceOrder]);

    // Fetch post-order timeline data (store orders only)
    useEffect(() => {
        const fetchTimeline = async () => {
            if (!id || isMarketplaceOrder) return;
            try {
                const response = await getOrderTimeline({ orderId: id });
                if (response?.success) {
                    setTimeline(response.data?.timeline || []);
                    setActiveExceptions(response.data?.activeExceptions || []);
                    setReturnRequests(response.data?.returnRequests || []);
                }
            } catch (error) {
                console.error("Error fetching timeline:", error);
            }
        };
        fetchTimeline();
    }, [id, isMarketplaceOrder]);

    // Post-Order action handlers
    const handleFlagException = async () => {
        if (!exceptionType) return;
        setFlaggingException(true);
        try {
            const response = await flagException({
                orderId: id,
                exceptionType,
                description: exceptionDescription,
                severity: exceptionSeverity
            });
            if (response?.success) {
                setShowExceptionModal(false);
                setExceptionType('');
                setExceptionDescription('');
                setExceptionSeverity('medium');
                // Refresh timeline
                const timelineResponse = await getOrderTimeline({ orderId: id });
                if (timelineResponse?.success) {
                    setTimeline(timelineResponse.data?.timeline || []);
                    setActiveExceptions(timelineResponse.data?.activeExceptions || []);
                }
            }
        } catch (error) {
            console.error("Error flagging exception:", error);
        } finally {
            setFlaggingException(false);
        }
    };

    const handleAddNote = async () => {
        if (!noteText.trim()) return;
        setAddingNote(true);
        try {
            const response = await addOrderNote({ orderId: id, note: noteText, isInternal: true });
            if (response?.success) {
                setShowNoteModal(false);
                setNoteText('');
                const timelineResponse = await getOrderTimeline({ orderId: id });
                if (timelineResponse?.success) {
                    setTimeline(timelineResponse.data?.timeline || []);
                }
            }
        } catch (error) {
            console.error("Error adding note:", error);
        } finally {
            setAddingNote(false);
        }
    };

    const handleResolveException = async () => {
        if (!selectedExceptionToResolve) return;
        setResolvingException(true);
        try {
            const response = await resolveException({
                orderId: id,
                exceptionId: selectedExceptionToResolve._id,
                resolution: resolveNotes,
                resolvedNotes: resolveNotes
            });
            if (response?.success) {
                setShowResolveModal(false);
                setSelectedExceptionToResolve(null);
                setResolveNotes('');
                // Refresh timeline and exceptions
                const timelineResponse = await getOrderTimeline({ orderId: id });
                if (timelineResponse?.success) {
                    setTimeline(timelineResponse.data?.timeline || []);
                    setActiveExceptions(timelineResponse.data?.activeExceptions || []);
                }
            }
        } catch (error) {
            console.error("Error resolving exception:", error);
        } finally {
            setResolvingException(false);
        }
    };

    // Helper to map API orderDetails to Invoice/Bill template format
    const mapOrderDetailsToTemplate = (orderDetails) => {
        if (!orderDetails) return null;
        console.log("Mapping Order Details:", orderDetails);

        return {
            id: orderDetails.saleId || orderDetails.orderId,
            date: formatDateToLong(orderDetails.orderDate || orderDetails.orderPlacedOn, true),
            status: orderDetails.status,
            customerName: orderDetails.customer?.customerName || orderDetails.addressInfo?.name || "",
            customerEmail: orderDetails.customer?.customerEmail || "",
            customerPhone: orderDetails.customer?.customerPhone || orderDetails.addressInfo?.phone || "",
            customerAddress: getFullAddress(orderDetails.addressInfo),
            
            // Delivery Information
            deliveryName: orderDetails.addressInfo?.name || orderDetails.customer?.customerName || "",
            deliveryPhone: orderDetails.addressInfo?.phone || orderDetails.customer?.customerPhone || "",
            deliveryAddress: getFullAddress(orderDetails.addressInfo),
            deliveryType: orderDetails.delivery?.deliveryOption?.type || orderDetails.delivery?.deliveryOption?.name || "Standard",
            deliveryDirections: orderDetails.addressInfo?.directions || "",
            
            items: orderDetails.productsInfo.filter((item) => !item.isCancelled).map((item) => ({
                name: item.productName,
                description: item.description,
                quantity: item.quantity,
                price: (item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0) * item.quantity,
                individualPrice: item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0,
                weight: item.selectedWeight?.weight || "",
                isCancelled: item.isCancelled || false,
            })),
            // Include cancelled items separately for reference
            cancelledItems: orderDetails.productsInfo.filter((item) => item.isCancelled).map((item) => ({
                name: item.productName,
                description: item.description,
                quantity: item.quantity,
                price: (item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0) * item.quantity,
                individualPrice: item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0,
                weight: item.selectedWeight?.weight || "",
                cancellationReason: item.cancellationReason || '',
                cancelledAt: item.cancelledAt,
            })),
            razorpayItems: orderDetails.productsInfo?.filter((item) => !item.isCancelled).map((item) => ({
                gstAmount: item.gstDetails?.totalGstAmount || 0,
                gstType: item.gstDetails?.gstType || "inclusive",
                cgstPercent: item.gstDetails?.gstPercentage/2 || 0,
                sgstPercent: item.gstDetails?.gstPercentage/2 || 0,
                cgstAmount: item.gstDetails?.cgst || 0,
                sgstAmount: item.gstDetails?.sgst || 0,
            })) || [],
            razorpay: {
                orginalTotalPrice: orderDetails.financial?.totalOriginalPrice || 0,
                couponDiscount: orderDetails.financial?.couponDiscount || 0,
                freeCashDiscount: orderDetails.financial?.freeCashDiscount || 0,
                items: orderDetails.productsInfo?.filter((item) => !item.isCancelled).map((item) => ({
                    couponDiscount: item.finalPricing?.couponDiscount || 0,
                })) || []
            },
            // Partial cancellation info
            hasPartialCancellation: orderDetails.hasPartialCancellation || false,
            gst: orderDetails.financial?.totalGstAmount || 0,
            total: orderDetails.financial?.finalAmount || 0,
            packagingCharges: orderDetails.financial?.deliveryCharges || 0,
            discount: orderDetails.financial?.totalDiscount || 0,
            couponDiscount: orderDetails.financial?.couponDiscount || 0,
            freeCashDiscount: orderDetails.financial?.freeCashDiscount || 0,
            totalAmount: orderDetails.financial?.finalAmount || 0,
            
            // GST Invoice Details for B2B Customers
            gstInvoice: orderDetails.gstInvoice?.requested ? {
                requested: true,
                gstin: orderDetails.gstInvoice.gstin,
                companyName: orderDetails.gstInvoice.companyName,
                state: orderDetails.gstInvoice.state,
                stateCode: orderDetails.gstInvoice.stateCode,
                email: orderDetails.gstInvoice.email,
                gstType: orderDetails.gstInvoice.gstType,
            } : null,
            
            // Branch Information for GST determination
            branchState: orderDetails.branch?.branchState || '',
            
            // Delivery State for GST determination (from addressInfo)
            deliveryState: orderDetails.addressInfo?.state || orderDetails.addressInfo?.addressState || '',
        };
    };


    // Calculate order total from API data
    const calculateOrderTotal = () => {
        if (!orderDetails) return 0;

        // Resolve delivery charges from financial object or delivery info
        const deliveryCharges = orderDetails.financial?.deliveryCharges ?? orderDetails.delivery?.deliveryOption?.price ?? 0;
        const couponDiscount = orderDetails.financial?.couponDiscount ?? 0;
        const freeCashDiscount = orderDetails.financial?.freeCashDiscount ?? 0;

        // Always recalculate from items to avoid doubled-subtotal bug in older multi-branch orders
        const allItems = orderDetails.productsInfo || [];
        const isOrderCancelled = orderDetails.status === 'Cancelled';
        const displayItems = isOrderCancelled
            ? allItems
            : allItems.filter(item => !item.isCancelled && !item.isReturned && (item.quantity || 1) > 0);

        const itemTotal = displayItems.reduce(
            (sum, item) => sum + ((item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0) * (item.quantity || 0)),
            0
        ) || 0;

        return itemTotal + (deliveryCharges || 0) - couponDiscount - freeCashDiscount;
    };


    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(null);

    // State for assign driver
    const [showAssignDriverModal, setShowAssignDriverModal] = useState(false);
    const [availableDrivers, setAvailableDrivers] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState("");
    const [loadingDrivers, setLoadingDrivers] = useState(false);

    const handleChangeStatus = async (newStatus) => {
        try {
            if (isMarketplaceOrder || orderDetails?.isMarketplaceOrder) {
                const response = await updateMarketplaceOrderStatus({
                    _id: orderDetails._id,
                    status: newStatus,
                });
                if (response?.success) {
                    setOrderDetails((prev) => ({
                        ...prev,
                        status: newStatus,
                    }));
                    setSelectedStatus(newStatus);
                    setShowStatusModal(false);
                    toast.success(`Order marked ${newStatus}`);
                    fetchOrderDetails();
                } else {
                    toast.error(response?.message || "Failed to update status");
                }
                return;
            }
            const payload = {
                _id: orderDetails._id,
                statusFrom: orderDetails.status,
                statusTo: newStatus,
            };
            const response = await changeOrderStatus(payload);
            if (response?.success) {
                setOrderDetails((prev) => ({ 
                    ...prev, 
                    status: newStatus,
                }));
                setSelectedStatus(newStatus);
                setShowStatusModal(false);
            } else {
                console.error("Failed to update status:", response?.message);
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error(error?.message || "Failed to update status");
        }
    };

    // Handle open assign driver modal
    const handleOpenAssignDriverModal = async () => {
        setShowAssignDriverModal(true);
        setSelectedDriverId("");
        try {
            setLoadingDrivers(true);
            const res = await getAvailableDrivers();
            if (res.success) {
                setAvailableDrivers(res.data || []);
            }
        } catch (err) {
            console.error("Failed to load drivers:", err);
        } finally {
            setLoadingDrivers(false);
        }
    };

    // Handle assign driver
    const handleAssignDriver = async () => {
        if (!selectedDriverId || !orderDetails?._id) return;
        try {
            const res = await assignDriverToOrder({
                orderId: orderDetails._id,
                driverId: selectedDriverId,
            });
            if (res.success) {
                const driver = availableDrivers.find(d => d._id === selectedDriverId);
                // Immediately update orderDetails in state so the badge appears without a re-fetch
                setOrderDetails(prev => ({
                    ...prev,
                    deliveryDriver: { driverName: driver?.name || '', driverPhone: driver?.phone || '' }
                }));
                toast.success("Driver assigned successfully!");
                setShowAssignDriverModal(false);
            } else {
                toast.error(res.message || "Failed to assign driver");
            }
        } catch (err) {
            toast.error("Failed to assign driver");
        }
    };

    // Handle individual item cancellation
    const handleCancelItem = async () => {
        if (!selectedCancelItem || !orderDetails?._id) return;
        
        try {
            setCancellingItem(true);
            const productId = selectedCancelItem._id || selectedCancelItem.productId;
            
            const response = await cancelOrderItem({
                orderId: orderDetails._id,
                productId: productId,
                itemId: selectedCancelItem.cartId,
                cancellationReason: itemCancellationReason || 'Cancelled by admin'
            });
            
            if (response?.success) {
                // Update order details with the returned order data
                if (response.data?.order) {
                    setOrderDetails(response.data.order);
                } else {
                    // Refresh order details from server
                    fetchOrderDetails();
                }
                
                // Close modal and reset state
                setShowCancelItemModal(false);
                setSelectedCancelItem(null);
                setItemCancellationReason('');
            } else {
                toast.error(response?.message || 'Failed to cancel item. Please try again.');
            }
        } catch (error) {
            console.error('Error cancelling item:', error);
            toast.error(error?.message || 'Failed to cancel item. Please try again.');
        } finally {
            setCancellingItem(false);
        }
    };

    // Check if an item can be cancelled
    const canCancelItem = (item) => {
        if (!orderDetails) return false;
        if (item.isCancelled) return false;
        
        // Can only cancel items in Received or Confirmed status
        const cancellableStatuses = ['Received', 'Confirmed', 'pending'];
        if (!cancellableStatuses.includes(orderDetails.status)) return false;
        
        // Count active items - can't cancel if it's the last one
        const activeItems = (orderDetails.productsInfo || []).filter(i => !i.isCancelled);
        if (activeItems.length <= 1) return false;
        
        return true;
    };

    // Get return info for an item by matching cartId or productId
    const getItemReturnInfo = (item) => {
        if (!returnRequests || returnRequests.length === 0) return null;
        for (const req of returnRequests) {
            const matchedItem = (req.items || []).find(ri =>
                (ri.cartId && item.cartId && ri.cartId.toString() === item.cartId.toString()) ||
                (ri.productId && item.productId && ri.productId.toString() === item.productId.toString())
            );
            if (matchedItem) {
                return { status: req.status, returnId: req.returnId, reason: matchedItem.reason || req.reason, quantity: matchedItem.quantity };
            }
        }
        return null;
    };


    // Handle individual item status change
    const handleUpdateItemStatus = async (newStatus) => {
        if (!selectedItemForStatus || !newStatus) return;
        
        setUpdatingItemStatus(true);
        try {
            const response = await updateItemStatus({
                orderId: orderDetails._id,
                productId: selectedItemForStatus._id || selectedItemForStatus.productId,
                status: newStatus
            });
            
            if (response?.success) {
                // Refresh order details to get updated item statuses
                await fetchOrderDetails();
                setShowItemStatusModal(false);
                setSelectedItemForStatus(null);
                
                // If global status was updated, update local state
                if (response.data?.globalStatusUpdated) {
                    setSelectedStatus(response.data.newGlobalStatus);
                }
            } else {
                toast.error(response?.message || 'Failed to update item status');
            }
        } catch (error) {
            console.error('Error updating item status:', error);
            toast.error(error?.message || 'Failed to update item status');
        } finally {
            setUpdatingItemStatus(false);
        }
    };

    // Get item status color based on status
    const getItemStatusColor = (status) => {
        const statusColors = {
            'Received': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
            'pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
            'Confirmed': { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200' },
            'Shipped': { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
            'Out For Delivery': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
            'Delivered': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
            'Cancelled': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' }
        };
        return statusColors[status] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };
    };

    // Normalize phone for WhatsApp wa.me link
    const normalizePhoneForWhatsApp = (phone) => {
        if (!phone) return null;
        const digits = ('' + phone).replace(/[^\d]/g, '');
        if (!digits) return null;
        // If 10 digits, assume India country code 91
        if (digits.length === 10) return '91' + digits;
        // If digits already include country code (e.g., 9198...), return as-is
        return digits;
    };

    const buildVendorMessage = (vendor, itemsForVendor) => {
        const lines = [];
        lines.push(`Order: ${orderDetails.saleId || orderDetails.orderId}`);
        lines.push(`Order Date: ${formatDateToLong(orderDetails.orderDate || orderDetails.orderPlacedOn, true)}`);
        lines.push('');
        // Delivery Information block (explicit)
        const deliveryName = orderDetails?.addressInfo?.name || orderDetails?.delivery?.addressDetails?.name || '';
        const deliveryPhone = orderDetails?.addressInfo?.phone || orderDetails?.delivery?.addressDetails?.phone || '';
        const deliveryAddress = getFullAddress(orderDetails.addressInfo) || (orderDetails?.delivery?.addressDetails ? getFullAddress(orderDetails.delivery.addressDetails) : '');
        const deliveryType = orderDetails?.delivery?.deliveryOption?.type || orderDetails?.delivery?.deliveryOption?.name || 'Standard';

        lines.push('Delivery Information:');
        lines.push(`Delivery Name: ${deliveryName || 'N/A'}`);
        lines.push(`Delivery Mobile: ${deliveryPhone || 'N/A'}`);
        lines.push(`Delivery Address: ${deliveryAddress || 'N/A'}`);
        lines.push(`Delivery Type: ${deliveryType}`);
        lines.push('');

        // Customer block (keeps backward compat)
        lines.push(`Customer: ${orderDetails.customer?.customerName || orderDetails.addressInfo?.name || 'N/A'}`);
        lines.push(`Customer Phone: ${orderDetails.customer?.customerPhone || orderDetails.addressInfo?.phone || 'N/A'}`);
        lines.push('');

        lines.push('Products for you:');

        let total = 0;
        itemsForVendor.forEach((it, idx) => {
            const weightLabel = typeof it.selectedWeight?.weight === 'object' && it.selectedWeight?.weight !== null
                ? it.selectedWeight.weight.weightLabel || `${it.selectedWeight.weight.weightValue} ${it.selectedWeight.weight.metricLabel}`
                : it.selectedWeight?.weight || '';
            const price = (it.selectedWeight?.discountPrice || it.pricing?.discountPrice || 0) * (it.quantity || 1);
            total += price;
            lines.push(`${idx + 1}. ${it.productName} ${weightLabel ? `(${weightLabel})` : ''} — Qty: ${it.quantity || 1}`);
        });
        lines.push('');
        lines.push(`Total for your items: ₹${total.toFixed(2)}`);
        lines.push('');
        lines.push('Please confirm availability and expected dispatch time.');

        return lines.join('\n');
    };

    const shareOrderToVendor = (vendorId) => {
        if (!orderDetails) return;
        const vendorIdStr = typeof vendorId === 'object' ? (vendorId.toString ? vendorId.toString() : vendorId) : vendorId;
        if (!vendorIdStr) {
            toast.error('Vendor ID missing');
            return;
        }
        // Gather all items for this vendor
        const itemsForVendor = (orderDetails.productsInfo || []).filter(it => {
            const pid = it._id || it.productId;
            const vId = it.vendorId || (it.vendorInfo && it.vendorInfo._id);
            const vIdStr = vId && (typeof vId === 'object' ? (vId.toString ? vId.toString() : vId) : vId);
            return vIdStr === vendorIdStr;
        });
        if (!itemsForVendor.length) {
            toast.error('No items found for this vendor');
            return;
        }
        // Resolve vendor info from the first item
        const vendor = itemsForVendor[0].vendorInfo || {};
        const phoneRaw = vendor.phone || itemsForVendor[0].vendorMobile || itemsForVendor[0].vendorPhone;
        const phone = normalizePhoneForWhatsApp(phoneRaw);
        if (!phone) {
            toast.error('Vendor phone not available to share via WhatsApp');
            return;
        }
        const message = buildVendorMessage(vendor, itemsForVendor);
        const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
    };


    // Check if item status can be changed
    const canChangeItemStatus = (item) => {
        if (!orderDetails) return false;
        if (item.isCancelled) return false;
        if (orderDetails.status === 'Cancelled') return false;
        if (orderDetails.status === 'Delivered') return false;
        return true;
    };

    // Fetch order details function
    const fetchOrderDetails = async () => {
        try {
            setLoading(true);

            if (isMarketplaceOrder) {
                const mpRes = await getMarketplaceOrders({ _id: id });
                if (mpRes?.success && mpRes.data?.length > 0) {
                    const adapted = adaptMarketplaceOrderForOverview(mpRes.data[0]);
                    setOrderDetails(adapted);
                    setSelectedStatus(adapted.status || "Received");
                } else {
                    setError("Marketplace order not found");
                    setOrderDetails(null);
                }
                return;
            }

            const response = await getOrders({ _id: id });
            if (response?.success && response.data?.length > 0) {
                const latestOrder = response.data[0];

                // Pull latest Shiprocket tracking on each refresh so activity log stays current.
                if (latestOrder?.delivery?.deliveryOption?.source === 'shiprocket' && latestOrder?._id) {
                    try {
                        const { getShiprocketTracking } = await import("../../services/ClientService");
                        await getShiprocketTracking({ orderId: latestOrder._id });
                        const refreshed = await getOrders({ _id: id });
                        if (refreshed?.success && refreshed.data?.length > 0) {
                            setOrderDetails(refreshed.data[0]);
                            setSelectedStatus(refreshed.data[0].status || "Received");
                            return;
                        }
                    } catch (trackingErr) {
                        console.warn('Shiprocket tracking refresh failed:', trackingErr?.message || trackingErr);
                    }
                }

                setOrderDetails(latestOrder);
                setSelectedStatus(latestOrder.status || "Received");
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
            setError(error?.message || 'Failed to load order');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateShiprocketTicket = () => {
        const shiprocketDetailsId = orderDetails?.shiprocket?.shiprocketOrderId || orderDetails?.shiprocket?.shipmentId;
        if (!shiprocketDetailsId) {
            toast.error("Shiprocket order details ID not found");
            return;
        }
        const url = `https://app.shiprocket.in/seller/orders/details/${shiprocketDetailsId}`;
        window.open(url, '_blank', 'noopener,noreferrer');
        toast.info("Opened Shiprocket order details. Use Create Ticket there.");
    };

    const handleCreateShiprocketReturn = async () => {
        if (!orderDetails?._id) return;
        const reason = window.prompt("Enter return reason", "Customer return request") || "";
        try {
            const { createShiprocketReturnOrder } = await import("../../services/ClientService");
            const res = await createShiprocketReturnOrder({
                orderId: orderDetails._id,
                returnReason: reason.trim(),
            });
            if (res?.success) {
                toast.success("Return order created successfully");
                fetchOrderDetails();
            } else {
                toast.error(res?.message || "Failed to create return order");
            }
        } catch (err) {
            toast.error(err?.message || "Failed to create return order");
        }
    };

    const formatShiprocketDateTime = (value) => {
        if (!value) return '-';
        try {
            const normalized = typeof value === 'string' ? value.replace(' ', 'T') : value;
            const dateObj = new Date(normalized);
            if (Number.isNaN(dateObj.getTime())) return String(value);
            return dateObj.toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            }).replace(',', '');
        } catch (e) {
            return String(value);
        }
    };

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Invoice_${orderDetails?.saleId || "Order"}`,
        pageStyle: `
          @media print {
            @page { margin: 20px; }
            body { -webkit-print-color-adjust: exact; }
          }
        `,
        onAfterPrint: () => {
            console.log("Print process completed successfully.");
        },
        onPrintError: (error) => {
            console.error("Error during print process:", error);
        },
    });

    const handleDownload = async () => {
        setIsGenerating(true);
        const input = componentRef.current;

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pages = input.querySelectorAll('.page-break');

            for (let i = 0; i < pages.length; i++) {
                const canvas = await html2canvas(pages[i], { scale: 1.5 });
                const imgData = canvas.toDataURL('image/png');
                const imgWidth = 210; // A4 width in mm
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'PNG', 10, 10, imgWidth - 20, imgHeight - 20); // Add margins
            }

            pdf.save(`Invoice_${orderDetails?.saleId || "Order"}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    // Map order details to thermal template format
    const mapOrderToThermalFormat = (orderDetails) => {
        if (!orderDetails) return null;
        
        // Format payment method for display
        const getPaymentMethodLabel = (paymentMode) => {
            const modes = {
                'cod': 'Cash on Delivery',
                'cash': 'Cash',
                'online': 'Online Payment',
                'card': 'Card Payment',
                'upi': 'UPI Payment',
                'razorpay': 'Online (Razorpay)',
                'wallet': 'Wallet'
            };
            return modes[paymentMode?.toLowerCase()] || paymentMode || 'Cash';
        };
        
        // Filter to billable items (non-cancelled, with quantity remaining after returns)
        const activeItems = (orderDetails.productsInfo || []).filter(
            item => !item.isCancelled && !item.isReturned && (item.quantity || 1) > 0
        );
        
        // Recalculate totals from active items only
        const originalTotal = activeItems.reduce((sum, item) => {
            const price = item.selectedWeight?.originalPrice || item.pricing?.originalPrice || 0;
            const quantity = item.quantity || 1;
            return sum + (price * quantity);
        }, 0);
        
        const discountedTotal = activeItems.reduce((sum, item) => {
            const price = item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0;
            const quantity = item.quantity || 1;
            return sum + (price * quantity);
        }, 0);
        
        const totalDiscount = originalTotal - discountedTotal;
        const deliveryCharges = orderDetails.financial?.deliveryCharges || 0;
        const couponDiscount = orderDetails.financial?.couponDiscount || 0;
        const freeCashDiscount = orderDetails.financial?.freeCashDiscount || 0;
        const totalGst = activeItems.reduce((sum, item) => sum + (item.gstDetails?.totalGstAmount || 0), 0);
        const finalAmount = discountedTotal - couponDiscount - freeCashDiscount + deliveryCharges;
        
        return {
            invoiceNumber: orderDetails.saleId || orderDetails.orderId || 'N/A',
            orderPlacedOn: orderDetails.orderDate || orderDetails.orderPlacedOn || new Date().toISOString(),
            salesPerson: orderDetails.branch?.branchName || 'Store',
            customerName: orderDetails.addressInfo?.name || orderDetails.customer?.customerName || 'Walk-in Customer',
            customerPhone: orderDetails.addressInfo?.phone || orderDetails.customer?.customerPhone || '',
            customerAddress: getFullAddress(orderDetails.addressInfo),
            deliveryType: orderDetails.delivery?.deliveryOption?.type || orderDetails.delivery?.deliveryOption?.name || 'Standard',
            paymentInfo: {
                method: getPaymentMethodLabel(orderDetails.payment?.paymentMode),
                status: orderDetails.payment?.paymentStatus || 'pending',
                transactionId: orderDetails.payment?.transactionId || null
            },
            items: activeItems.map((item) => {
                const weight = item.selectedWeight?.weight;
                const weightStr = typeof weight === 'object' && weight !== null
                    ? weight.weightLabel || `${weight.weightValue} ${weight.metricLabel}`
                    : weight || 'N/A';
                const discountPrice = item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0;
                const originalPrice = item.selectedWeight?.originalPrice || item.pricing?.originalPrice || discountPrice;
                const quantity = item.quantity || 1;
                const total = discountPrice * quantity;
                const gstAmount = item.gstDetails?.totalGstAmount || 0;
                
                return {
                    productName: item.productName,
                    selectedOption: {
                        weight: weightStr,
                        originalPrice: originalPrice,
                        discountPrice: discountPrice
                    },
                    quantity: quantity,
                    price: discountPrice,
                    total: total,
                    tax: gstAmount,
                    hsnCode: item.hsnCode || '',
                    gstGroup: {
                        cgst: item.gstDetails?.gstPercentage ? item.gstDetails.gstPercentage / 2 : 0,
                        sgst: item.gstDetails?.gstPercentage ? item.gstDetails.gstPercentage / 2 : 0,
                        percentage: item.gstDetails?.gstPercentage || 0,
                        type: item.gstDetails?.gstType || 'inclusive'
                    }
                };
            }),
            financial: {
                subtotal: discountedTotal,
                taxInclusive: totalGst,
                taxExclusive: 0,
                deliveryCharges: deliveryCharges,
                couponDiscount: couponDiscount,
                totalDiscount: totalDiscount,
                finalAmount: finalAmount
            }
        };
    };

    // Handle thermal bill print using Template Manager settings
    const handlePrintThermalBill = () => {
        if (!orderDetails) return;
        
        const thermalOrderData = mapOrderToThermalFormat(orderDetails);
        const htmlContent = generateReceipt(thermalOrderData, templatePreferences.templateId, templatePreferences.billSize);
        
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    // Legacy handlePrintBill using BillTemplate (kept for backward compatibility if needed)
    const handlePrintBill = useReactToPrint({
        contentRef: billRef,
        documentTitle: `Bill_${orderDetails?.saleId || "Order"}`,
        pageStyle: `
          @media print {
            @page { margin: 10px; }
            body { -webkit-print-color-adjust: exact; }
          }
        `,
        onAfterPrint: () => {
            console.log("Bill print process completed successfully.");
        },
        onPrintError: (error) => {
            console.error("Error during bill print process:", error);
        },
    });

    // Helper to format full address from addressInfo
    const getFullAddress = (addressInfo) => {
        if (!addressInfo) return "N/A";

        // Priority: use the full Google geocoded string (addressInfo.address) — richest representation.
        // Both normal customer orders and custom orders now store this field.
        // Prepend house/building only if it isn't already included in the geocoded string.
        if (addressInfo.address) {
            const house = (addressInfo.house || '').trim();
            if (house && !addressInfo.address.includes(house)) {
                return `${house}, ${addressInfo.address}`;
            }
            return addressInfo.address;
        }

        // Fallback: build from structured fields (old orders without geocoded address string)
        const parts = [
            addressInfo.house,
            addressInfo.area,
            addressInfo.landmark,
            addressInfo.city,
            addressInfo.pincode,
            addressInfo.state,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : (addressInfo.addressLine1 || "N/A");
    };

    // Format payment method for display (used in Delivery Modal and sharing)
    const getPaymentMethodLabel = (paymentMode) => {
        const modes = {
            'cod': 'Cash on Delivery',
            'cash': 'Cash',
            'online': 'Online Payment',
            'card': 'Card Payment',
            'upi': 'UPI Payment',
            'razorpay': 'Online (Razorpay)',
            'wallet': 'Wallet'
        };
        return modes[paymentMode?.toLowerCase()] || paymentMode || 'Cash';
    };

    // Delivery details text for sharing (with/without items and bill)
    const getDeliveryDetailsText = () => {
        if (!orderDetails) return "";
        const addressInfo = orderDetails.addressInfo || {};
        const customer = orderDetails.customer || {};
        const fullAddress = getFullAddress(addressInfo);
        const mapsLink = addressInfo.address
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressInfo.address || "")}`
            : "";
        const deliveryType = orderDetails?.delivery?.deliveryOption?.type || orderDetails?.delivery?.deliveryOption?.name || "Standard";

        const paymentLabel = getPaymentMethodLabel(orderDetails?.payment?.paymentMode || orderDetails?.payment?.method || orderDetails?.payment?.paymentType);

        let text =
            `*Delivery Information*\n` +
            `Order ID: ${orderDetails.saleId || orderDetails.orderId}\n` +
            `Name: ${addressInfo.name || "N/A"}\n` +
            `Mobile: ${addressInfo.phone || "N/A"}\n` +
            `Delivery Type: ${deliveryType}\n` +
            `Payment Type: ${paymentLabel}\n` +
            `\n*Delivery Address*\n${fullAddress}\n` +
            (mapsLink ? `\n*Navigate:* ${mapsLink}\n` : "") +
            (addressInfo.directions ? `\n*Directions:* ${addressInfo.directions}\n` : "");

        // Add customer details as optional (if different from delivery)
        if (customer.customerName || customer.customerPhone) {
            text += `\n*Customer Details (Secondary Details)*\n`;
            if (customer.customerName) {
                text += `Name: ${customer.customerName}\n`;
            }
            if (customer.customerPhone) {
                text += `Phone: ${customer.customerPhone}\n`;
            }
        }

        if (includeDetails) {
            // Only include active (non-cancelled) items
            const activeItems = orderDetails.productsInfo.filter(
                item => !item.isCancelled && !item.isReturned && (item.quantity || 1) > 0
            );
            const items = activeItems
                .map(
                    (item, idx) => {
                        const weight = item.selectedWeight?.weight;
                        const weightStr = typeof weight === 'object' && weight !== null
                            ? weight.weightLabel || `${weight.weightValue} ${weight.metricLabel}`
                            : weight || 'N/A';
                        return `   • ${item.productName} (${weightStr}) x${item.quantity}`;
                    }
                )
                .join('\n');
            text +=
                `\n*Items*\n${items}` +
                `\n\n*Total Bill:* ₹${calculateOrderTotal()}`;
        }
        return text;
    };

    // WhatsApp share link (address as link)
    const getWhatsAppShareLink = () => {
        const text = encodeURIComponent(getDeliveryDetailsText());
        return `https://wa.me/?text=${text}`;
    };

    // SMS share link
    const getSMSShareLink = () => {
        const text = encodeURIComponent(getDeliveryDetailsText());
        return `sms:?body=${text}`;
    };

    // Google Maps link for address (use full address)
    const getGoogleMapsLink = () => {
        const addressInfo = orderDetails?.addressInfo || {};
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressInfo.address || "")}`;
    };

    // Copy to clipboard
    const handleCopyDeliveryDetails = () => {
        navigator.clipboard.writeText(getDeliveryDetailsText());
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
    };

    // Handle transfer completion
    const handleTransferComplete = (result) => {
        if (result.success) {
            toast.success(result.message || 'Order transferred successfully!');
            // Navigate back to orders page
            navigate('/orders');
        }
    };

    // Create order object for transfer modal
    const getOrderForTransfer = () => {
        if (!orderDetails) return null;
        
        // Filter out cancelled items - only transfer active items
        const activeProducts = (orderDetails.productsInfo || []).filter(item => !item.isCancelled);
        
        return {
            _id: orderDetails._id,
            saleId: orderDetails.saleId || orderDetails.orderId,
            itemsCount: activeProducts.length,
            totalAmount: orderDetails.financial?.finalAmount || 0,
            status: orderDetails.status || orderDetails.orderStatus,
            productsInfo: activeProducts,
            branchId: orderDetails.branch?.branchId,
            branchName: orderDetails.branch?.branchName,
            hasPartialCancellation: orderDetails.hasPartialCancellation || false
        };
    };

    if (loading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-white/80 z-50">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-r from-indigo-200 to-blue-100">
                <div className="text-xl font-bold text-red-600">{error}</div>
            </div>
        );
    }

    return (
        <>
        <div className="h-screen overflow-hidden max-h-fit bg-gradient-to-r from-indigo-200 to-blue-100">
            {/* Sticky Header */}
            <NavigateBack targetBack className="pl-3" />
            <div className="bg-white p-6 rounded-tl-2xl rounded-tr-2xl sticky top-0 z-10">
                <div className="flex justify-between items-center">
                    <h1 className="text-3xl font-bold">
                        {isMarketplaceOrder || orderDetails?.isMarketplaceOrder ? 'Marketplace Order Overview' : 'Order Overview'}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 justify-end">
                        {!(isMarketplaceOrder || orderDetails?.isMarketplaceOrder) && (
                        <>
                        {/* Post-Order Actions */}
                        <button
                            onClick={() => setShowTimelinePanel(!showTimelinePanel)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                                showTimelinePanel ? 'bg-indigo-600 text-white' : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'
                            }`}
                            title="View order timeline"
                        >
                            <BsGraphUp className="text-lg" />
                            Timeline {activeExceptions.length > 0 && <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{activeExceptions.length}</span>}
                        </button>
                        <button
                            onClick={() => setShowExceptionModal(true)}
                            className="flex items-center gap-2 bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg transition-all duration-300"
                            title="Flag an exception"
                        >
                            <FaExclamationTriangle className="text-lg" />
                            Flag Issue
                        </button>
                        <button
                            onClick={() => setShowNoteModal(true)}
                            className="flex items-center gap-2 bg-amber-100 hover:bg-amber-200 text-amber-700 px-4 py-2 rounded-lg transition-all duration-300"
                            title="Add internal note"
                        >
                            <FiMessageCircle className="text-lg" />
                            Add Note
                        </button>
                        {/* Transfer Order Button */}
                        <button
                            onClick={() => setShowTransferModal(true)}
                            className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition-all duration-300"
                            title="Transfer order to another branch"
                        >
                            <FaExchangeAlt className="text-lg" />
                            Transfer Order
                        </button>
                        </>
                        )}

                        {!(isMarketplaceOrder || orderDetails?.isMarketplaceOrder) && orderDetails?.status === 'Delivered' && (
                            <>
                                <button
                                    onClick={() => setShowReturnRequestModal(true)}
                                    className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg transition-all duration-300"
                                    title="Submit a return request for approval and refund workflow"
                                >
                                    <FaUndoAlt className="text-lg" />
                                    Raise Return Request
                                </button>
                                <button
                                    onClick={() => setShowReturnRestoreModal(true)}
                                    className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-all duration-300"
                                    title="Customer returned product at delivery — restore inventory and update billing"
                                >
                                    <FaUndoAlt className="text-lg" />
                                    Return and Restore
                                </button>
                            </>
                        )}
                        
                        {/* Share Delivery Details Button */}
                        <button
                            onClick={() => setShowDeliveryModal(true)}
                            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-all duration-300"
                            title="Share delivery details"
                        >
                            <FiShare2 className="text-lg" />
                            Share Delivery Details
                        </button>
                        <button
                            onClick={handleDownload}
                            disabled={isGenerating}
                            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-all duration-300 disabled:opacity-50"
                        >
                            <FiDownload className="text-lg" />
                            {isGenerating ? 'Generating...' : 'Download Invoice'}
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg transition-all duration-300"
                        >
                            <FiPrinter className="text-lg" />
                            Print Invoice
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="overflow-y-auto h-full max-h-[calc(100vh-425px)] md:max-h-[calc(100vh-210px)] px-6 pt-2 pb-6 max-md:pb-28 bg-white scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-gray-400 scrollbar-track-gray-200 hover:scrollbar-thumb-gray-500">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column for Order, Customer, and Bill Details */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Order Summary */}
                        <motion.div
                            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <div className="flex items-center space-x-3">
                                    <BsBoxSeam className="text-3xl text-blue-600" />
                                    <h2 className="text-xl font-bold text-gray-800">Order Summary</h2>
                                </div>
                                <div className="flex items-center gap-4">
                                    {/* Assign Driver Button - store orders only */}
                                    {!(isMarketplaceOrder || orderDetails?.isMarketplaceOrder) && (orderDetails.status === "Confirmed" || orderDetails.status === "Shipped") && !orderDetails.isManualDelivery && !orderDetails.deliveryDriver && (
                                        <button
                                            onClick={handleOpenAssignDriverModal}
                                            className="flex items-center gap-1 text-green-600 hover:text-green-700 font-semibold text-sm transition-colors"
                                            title="Assign delivery driver"
                                        >
                                            <FaTruck className="text-sm" />
                                            <span>Assign Driver</span>
                                        </button>
                                    )}
                                    <div className="relative">
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowStatusModal(true);
                                        }}
                                        className="relative cursor-pointer text-yellow-600 font-semibold text-md underline inline-block overflow-hidden group"
                                    >
                                        Change Status
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-200 to-transparent opacity-0 w-full h-full animate-light-slide group-hover:opacity-60 transition-opacity duration-300 pointer-events-none"></div>
                                    </span>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2 text-md text-gray-700">
                                <p><span className="font-semibold">Order ID:</span> {orderDetails.saleId || orderDetails.orderId}</p>
                                <p><span className="font-semibold">Order Date:</span> {formatDateToLong(orderDetails.orderDate || orderDetails.orderPlacedOn, true)}</p>
                                <p>
                                    <span className="font-semibold">Status:</span>{" "}
                                    <span
                                        className={`font-bold py-1 px-3 rounded-full text-sm ${(orderDetails.status) === "Delivered"
                                            ? "bg-green-100 text-green-700"
                                            : (orderDetails.status || orderDetails.status) === "Received"
                                                ? "bg-yellow-100 text-yellow-700"
                                                : (orderDetails.status || orderDetails.status) === "Confirmed"
                                                    ? "bg-blue-100 text-blue-700"
                                                    : (orderDetails.status || orderDetails.status) === "Cancelled"
                                                        ? "bg-red-100 text-red-700"
                                                        : "bg-blue-100 text-blue-700"
                                            }`}
                                    >
                                        {(orderDetails.status) === "Received" ? "Received" : (orderDetails.status)}
                                    </span>
                                    {orderDetails.status === "Cancelled" && orderDetails.cancellationInfo?.cancelledBy === "customer" && (
                                        <span className="ml-2 text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded-md border border-red-200">
                                            ⚠️ Cancelled by Customer
                                        </span>
                                    )}
                                    {orderDetails.isManualDelivery && (
                                        <span className="ml-2 text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded-md border border-orange-200">
                                            🚚 Manual Delivery
                                        </span>
                                    )}
                                </p>
                                {orderDetails && (
                                    <div className="mt-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
                                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Status timeline</p>
                                        <div className="flex items-center gap-0.5 min-w-max text-[10px] leading-tight text-gray-600">
                                            {buildStatusTimelineEntries(orderDetails).map((e, idx) => (
                                                <Fragment key={`${e.label}-${idx}-${e.at}`}>
                                                    {idx > 0 && (
                                                        <span className="text-gray-300 px-0.5 shrink-0" aria-hidden>
                                                            →
                                                        </span>
                                                    )}
                                                    <span className="inline-flex flex-col sm:flex-row sm:items-center sm:gap-1 whitespace-nowrap px-1.5 py-0.5 rounded bg-gray-50 border border-gray-100">
                                                        <span className="font-semibold text-gray-700">{e.label}</span>
                                                        {e.at && (
                                                            <>
                                                                <span className="hidden sm:inline text-gray-300">·</span>
                                                                <span className="text-gray-500 tabular-nums">{formatDateToLong(e.at, true)}</span>
                                                            </>
                                                        )}
                                                    </span>
                                                </Fragment>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {orderDetails.deliveryDriver?.driverName && (
                                    <p className="flex items-center gap-2">
                                        <span className="font-semibold">Assigned Driver:</span>
                                        <span className="inline-flex items-center gap-1.5 text-green-700 font-semibold bg-green-50 px-2.5 py-0.5 rounded-full text-sm border border-green-200">
                                            <FaTruck className="text-xs" />
                                            {orderDetails.deliveryDriver.driverName}
                                            {orderDetails.deliveryDriver.driverPhone ? <span className="text-green-600 font-normal">· {orderDetails.deliveryDriver.driverPhone}</span> : null}
                                        </span>
                                    </p>
                                )}
                                {orderDetails.status === "Cancelled" && orderDetails.cancellationInfo?.cancelledBy === "customer" && orderDetails.cancellationInfo?.cancellationReason && (
                                    <div className="mt-2 p-3 bg-red-50 border-l-4 border-red-500 rounded">
                                        <p className="text-sm font-semibold text-red-800">Cancellation Reason:</p>
                                        <p className="text-sm text-red-700 mt-1">{orderDetails.cancellationInfo.cancellationReason}</p>
                                        {orderDetails.cancellationInfo?.cancelledAt && (
                                            <p className="text-xs text-red-600 mt-1">
                                                Cancelled on: {formatDateToLong(orderDetails.cancellationInfo.cancelledAt, true)}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>

                        {/* Customer Details */}
                        <motion.div
                            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                        >
                            <div className="flex items-center space-x-3 mb-5">
                                <AiOutlineUser className="text-3xl text-blue-600" />
                                <h2 className="text-xl font-bold text-gray-800">
                                    {(isMarketplaceOrder || orderDetails?.isMarketplaceOrder) ? 'Seller Information' : 'Customer Information'}
                                </h2>
                            </div>
                            <div className="space-y-2 text-md text-gray-700">
                                <p>
                                    <span className="font-semibold">Name:</span> {orderDetails?.customer?.customerName || orderDetails?.addressInfo?.name || "N/A"}
                                </p>
                                <p>
                                    <span className="font-semibold">Phone:</span> {orderDetails?.customer?.customerPhone || orderDetails?.addressInfo?.phone || "N/A"}
                                </p>
                                {orderDetails?.customer?.customerEmail ? (
                                <p>
                                    <span className="font-semibold">Email:</span> {orderDetails.customer.customerEmail}
                                </p>
                                ) : !(isMarketplaceOrder || orderDetails?.isMarketplaceOrder) ? (
                                <p>
                                    <span className="font-semibold">Email:</span> N/A
                                </p>
                                ) : null}
                                {(isMarketplaceOrder || orderDetails?.isMarketplaceOrder) && orderDetails?.customer?.gstNumber && (
                                <p>
                                    <span className="font-semibold">GSTIN:</span> {orderDetails.customer.gstNumber}
                                </p>
                                )}
                            </div>
                        </motion.div>

                        {/* Delivery Information */}
                        <motion.div
                            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                        >
                            <div className="flex items-center space-x-3 mb-5">
                                <FiMapPin className="text-3xl text-blue-600" />
                                <h2 className="text-xl font-bold text-gray-800">Delivery Information</h2>
                            </div>
                            <div className="space-y-2 text-md text-gray-700">
                                <p>
                                    <span className="font-semibold">Delivery Name:</span> {orderDetails?.addressInfo?.name || "N/A"}
                                </p>
                                <p>
                                    <span className="font-semibold">Delivery Mobile:</span> {orderDetails?.addressInfo?.phone || "N/A"}
                                </p>
                                <p>
                                    <span className="font-semibold">Delivery Address:</span> {getFullAddress(orderDetails?.addressInfo)}
                                </p>
                                <p>
                                    <span className="font-semibold">Delivery Type:</span>{" "}
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {orderDetails?.delivery?.deliveryOption?.type || "Standard"}
                                    </span>
                                </p>
                                <p>
                                    <span className="font-semibold">Payment Type:</span>{" "}
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        {getPaymentMethodLabel(orderDetails?.payment?.paymentMode) || "N/A"}
                                    </span>
                                </p>
                                {orderDetails?.addressInfo?.directions && (
                                    <p>
                                        <span className="font-semibold">Directions:</span> {orderDetails?.addressInfo?.directions}
                                    </p>
                                )}
                            </div>
                        </motion.div>

                        {/* GST Invoice Details - Only show if customer requested GST Invoice */}
                        {orderDetails?.gstInvoice?.requested && (
                            <motion.div
                                className="bg-white rounded-xl shadow-lg p-6 border border-green-200 hover:shadow-xl transition-shadow duration-300 bg-gradient-to-r from-green-50 to-emerald-50"
                            >
                                <div className="flex items-center justify-between mb-5">
                                    <div className="flex items-center space-x-3">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-800">GST Invoice Details</h2>
                                            <span className="text-xs text-green-600 font-medium">B2B Tax Invoice Requested</span>
                                        </div>
                                    </div>
                                    {/* Checkbox to include GSTIN in Invoice */}
                                    <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-lg border border-green-200 hover:bg-green-50 transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={includeGstinInInvoice}
                                            onChange={() => setIncludeGstinInInvoice(v => !v)}
                                            className="accent-green-600 w-4 h-4 rounded border-gray-300"
                                        />
                                        <span className="text-sm text-gray-700 select-none">Include in Invoice</span>
                                    </label>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3 text-md text-gray-700">
                                        <p className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-800 min-w-[100px]">GSTIN:</span>
                                            <span className="font-mono bg-gray-100 px-3 py-1 rounded text-gray-900 font-semibold tracking-wide">
                                                {orderDetails.gstInvoice.gstin}
                                            </span>
                                        </p>
                                        {orderDetails.gstInvoice.companyName && (
                                            <p className="flex items-start gap-2">
                                                <span className="font-semibold text-gray-800 min-w-[100px]">Company:</span>
                                                <span className="text-gray-700">{orderDetails.gstInvoice.companyName}</span>
                                            </p>
                                        )}
                                        <p className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-800 min-w-[100px]">State:</span>
                                            <span className="text-gray-700">
                                                {orderDetails.gstInvoice.stateCode && (
                                                    <span className="font-mono text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mr-2">
                                                        {orderDetails.gstInvoice.stateCode}
                                                    </span>
                                                )}
                                                {orderDetails.gstInvoice.state}
                                            </span>
                                        </p>
                                        <p className="flex items-center gap-2">
                                            <span className="font-semibold text-gray-800 min-w-[100px]">Email:</span>
                                            <span className="text-blue-600">{orderDetails.gstInvoice.email}</span>
                                        </p>
                                    </div>
                                    {/* <div className="flex items-center justify-center">
                                        <div className={`px-4 py-2 rounded-lg font-semibold text-sm ${
                                            orderDetails.gstInvoice.gstType === 'IGST' 
                                                ? 'bg-orange-100 text-orange-700 border border-orange-200' 
                                                : 'bg-blue-100 text-blue-700 border border-blue-200'
                                        }`}>
                                            <div className="text-center">
                                                <span className="block text-xs uppercase tracking-wider mb-1">GST Type</span>
                                                <span className="block text-lg font-bold">
                                                    {orderDetails.gstInvoice.gstType === 'IGST' 
                                                        ? '🚚 Inter-State (IGST)' 
                                                        : '🏠 Intra-State (CGST+SGST)'}
                                                </span>
                                            </div>
                                        </div>
                                    </div> */}
                                </div>
                            </motion.div>
                        )}

                        {/* Order Items */}
                        <motion.div
                            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                        >
                            <div className="flex items-center space-x-3 mb-5">
                                <AiOutlineShoppingCart className="text-3xl text-blue-600" />
                                <h2 className="text-xl font-bold text-gray-800">Ordered Items (
                                    {orderDetails.productsInfo.filter(item => !item.isCancelled && !item.isReturned && (item.quantity || 1) > 0).reduce(
                                        (sum, item) => sum + item.quantity,
                                        0)}
                                    )
                                </h2>
                                {orderDetails.hasPartialCancellation && (
                                    <span className="ml-2 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                                        Partial Cancellation
                                    </span>
                                )}
                                {returnRequests.length > 0 && (
                                    <span className="ml-2 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full flex items-center gap-1">
                                        <FaUndoAlt className="w-3 h-3" />
                                        Return Requested
                                    </span>
                                )}
                            </div>
                            <div className="space-y-4">
                                {orderDetails.productsInfo.map((item) => (
                                    <div
                                        key={item.productId || item._id}
                                        className={`flex flex-col pb-4 border-b border-gray-200 last:border-none last:pb-0 ${item.isCancelled ? 'opacity-60' : ''}`}
                                    >
                                        {/* Cancelled Badge */}
                                        {item.isCancelled && (
                                            <div className="mb-2">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-100 text-red-700 border border-red-200">
                                                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                    Cancelled
                                                </span>
                                            </div>
                                        )}

                                        {/* Return Status Badge */}
                                        {(() => {
                                            const returnInfo = getItemReturnInfo(item);
                                            if (!returnInfo || item.isCancelled) return null;
                                            const returnStatusStyles = {
                                                'Requested': 'bg-orange-100 text-orange-700 border-orange-200',
                                                'Approved': 'bg-blue-100 text-blue-700 border-blue-200',
                                                'Pickup Scheduled': 'bg-purple-100 text-purple-700 border-purple-200',
                                                'Picked Up': 'bg-indigo-100 text-indigo-700 border-indigo-200',
                                                'QC Pending': 'bg-amber-100 text-amber-700 border-amber-200',
                                                'QC Passed': 'bg-emerald-100 text-emerald-700 border-emerald-200',
                                                'QC Failed': 'bg-red-100 text-red-700 border-red-200',
                                                'Refund Initiated': 'bg-yellow-100 text-yellow-700 border-yellow-200',
                                                'Refund Completed': 'bg-green-100 text-green-700 border-green-200',
                                                'Rejected': 'bg-red-100 text-red-700 border-red-200',
                                                'Closed': 'bg-gray-100 text-gray-700 border-gray-200',
                                            };
                                            const style = returnStatusStyles[returnInfo.status] || 'bg-orange-100 text-orange-700 border-orange-200';
                                            return (
                                                <div className="mb-2 flex items-center gap-2 flex-wrap">
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${style} border`}>
                                                        <FaUndoAlt className="w-3 h-3 mr-1" />
                                                        Return {returnInfo.status}
                                                    </span>
                                                    {returnInfo.returnId && (
                                                        <span className="text-xs text-gray-500">{returnInfo.returnId}</span>
                                                    )}
                                                    {returnInfo.reason && (
                                                        <span className="text-xs text-gray-400 italic">— {returnInfo.reason}</span>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        
                                        {/* Item Status Badge - only show for active items */}
                                        {!item.isCancelled && (
                                            <div className="mb-2 flex items-center gap-2 flex-wrap">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${getItemStatusColor(item.itemStatus || orderDetails.status).bg} ${getItemStatusColor(item.itemStatus || orderDetails.status).text} ${getItemStatusColor(item.itemStatus || orderDetails.status).border} border`}>
                                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {item.itemStatus || orderDetails.status}
                                                </span>
                                                
                                                {/* Item Status Change Button */}
                                                {canChangeItemStatus(item) && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedItemForStatus(item);
                                                            setShowItemStatusModal(true);
                                                        }}
                                                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                                                    >
                                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                        Change Status
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                {(() => {
                                                    const img =
                                                        (typeof item.images?.preview === 'string' && item.images.preview)
                                                        || (typeof item.image === 'string' && item.image)
                                                        || item.selectedWeight?.images?.[0]?.preview
                                                        || null;
                                                    return (
                                                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0">
                                                            {img ? (
                                                                <img src={img} alt={item.productName} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">No img</div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            <div className="space-y-1 mb-2 sm:mb-0 flex-1 min-w-0">
                                                <p className={`text-lg font-semibold leading-tight line-clamp-1 ${item.isCancelled ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                                    {item.productName}
                                                    {(() => {
                                                        const w = item.selectedWeight?.weight;
                                                        const label = typeof w === 'object' && w !== null
                                                            ? (w.weightLabel || [w.weightValue, w.metricLabel].filter(Boolean).join(' '))
                                                            : (w || item.selectedWeight?.weightLabel || item.description || '');
                                                        if (!label || label === 'N/A') return null;
                                                        return (
                                                            <span className={`ml-2 text-base font-medium ${item.isCancelled ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                ({label})
                                                            </span>
                                                        );
                                                    })()}
                                                </p>
                                                {item.description && item.description !== (typeof item.selectedWeight?.weight === 'object' ? item.selectedWeight?.weight?.weightLabel : item.selectedWeight?.weight) && (
                                                <p className="text-sm text-gray-600 leading-snug line-clamp-1">
                                                    {item.description}
                                                </p>
                                                )}
                                                {(() => {
                                                    const expiryInfo = getItemExpiryDisplay(orderDetails, item);
                                                    if (!expiryInfo) return null;
                                                    return (
                                                        <p className={`text-sm mt-1 ${expiryInfo.colorClass}`}>
                                                            {expiryInfo.text}
                                                        </p>
                                                    );
                                                })()}
                                            </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="text-right pl-2 flex items-center space-x-2">
                                                    <p className={`font-bold text-xl ${item.isCancelled ? 'text-gray-400' : 'text-gray-800'}`}>x {item.quantity}</p>
                                                    <p className={`text-lg font-semibold ${item.isCancelled ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                                                        ₹{((item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0) * item.quantity).toFixed(2)}
                                                    </p>
                                                </div>
                                                {/* Cancel Item Button */}
                                                {canCancelItem(item) && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedCancelItem(item);
                                                            setShowCancelItemModal(true);
                                                        }}
                                                        className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors flex items-center gap-1"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                        Cancel
                                                    </button>
                                                )}
                                                
                                                {/* Vendor Info Toggle Button */}
                                                {item.vendorInfo && (
                                                    <button
                                                        onClick={() => toggleVendorAccordion(item.productId || item._id)}
                                                        className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors flex items-center gap-1"
                                                    >
                                                        <svg className={`w-3.5 h-3.5 transition-transform ${expandedVendorItems[item.productId || item._id] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                        Vendor
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Vendor Details Accordion */}
                                        {item.vendorInfo && expandedVendorItems[item.productId || item._id] && (
                                            <div className="mt-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200 animate-fadeIn">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                    </svg>
                                                    <h4 className="font-semibold text-purple-800">Vendor Details</h4>
                                                    <div className="ml-auto flex items-center gap-2">
                                                        <button
                                                                onClick={() => shareOrderToVendor(item.vendorId || (item.vendorInfo && item.vendorInfo._id))}
                                                                className="px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 border border-green-100 rounded-lg transition-colors flex items-center gap-2"
                                                            >
                                                                <FaWhatsapp /> Share Order Details
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                                    {/* Vendor Name */}
                                                    <div className="flex items-start gap-2">
                                                        <span className="text-gray-500 min-w-[80px]">Name:</span>
                                                        <span className="font-medium text-gray-800">{item.vendorInfo?.name || 'N/A'}</span>
                                                    </div>
                                                    
                                                    {/* Vendor Email */}
                                                    {item.vendorInfo.email && (
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-gray-500 min-w-[80px]">Email:</span>
                                                            <a href={`mailto:${item.vendorInfo.email}`} className="font-medium text-blue-600 hover:underline">
                                                                {item.vendorInfo.email}
                                                            </a>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Vendor Phone */}
                                                    {item.vendorInfo.phone && (
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-gray-500 min-w-[80px]">Phone:</span>
                                                            <a href={`tel:${item.vendorInfo.phone}`} className="font-medium text-blue-600 hover:underline">
                                                                {item.vendorInfo.phone}
                                                            </a>
                                                        </div>
                                                    )}
                                                    
                                                    {/* GST Number */}
                                                    {item.vendorInfo.gstNumber && (
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-gray-500 min-w-[80px]">GST No:</span>
                                                            <span className="font-medium text-gray-800">{item.vendorInfo.gstNumber}</span>
                                                        </div>
                                                    )}
                                                    
                                                    {/* HSN Number (from product) */}
                                                    {item.hsnNumber && (
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-gray-500 min-w-[80px]">HSN No:</span>
                                                            <span className="font-medium text-gray-800">{item.hsnNumber}</span>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Vendor Address */}
                                                    {item.vendorInfo.address && (
                                                        <div className="flex items-start gap-2 sm:col-span-2">
                                                            <span className="text-gray-500 min-w-[80px]">Address:</span>
                                                            <span className="font-medium text-gray-800">{item.vendorInfo.address}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* Right Column for Bill Details and Analytics */}
                    <div className="lg:col-span-1 space-y-8">
                        <div className="flex items-center gap-2 mt-2 text-gray-500 text-xs">
                            <span className="text-blue-500">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                                </svg>
                            </span>
                            <p>Note: While printing, please ensure to set margins to "None" in the print settings for best results.</p>
                        </div>
                        {/* Bill Details */}
                        <motion.div
                            className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
                        >
                            <div className="flex items-center space-x-3 mb-5">
                                <CiMoneyBill className="text-3xl text-blue-600" />
                                <h2 className="text-xl font-bold text-gray-800">Bill Details</h2>
                                {orderDetails.hasPartialCancellation && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                                        Partial Cancel
                                    </span>
                                )}
                                {orderDetails.hasPartialReturn && (
                                    <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                                        Partial Return
                                    </span>
                                )}
                            </div>

                            <div className="space-y-3 text-md">
                                {/* Calculate values - use backend values for cancelled orders, recalculate for active */}
                                {(() => {
                                    const allItems = orderDetails.productsInfo || [];
                                    const orderStatus = orderDetails.status;
                                    const isOrderCancelled = orderStatus === 'Cancelled';
                                    const billSummary = orderDetails.financial?.billSummary;

                                    const getBillableItems = (items) => items.filter(
                                        (item) => !item.isCancelled && !item.isReturned && (item.quantity || 0) > 0
                                    );

                                    const sumFromItems = (items, priceKey) => items.reduce((sum, item) => {
                                        const price = priceKey === 'original'
                                            ? (item.selectedWeight?.originalPrice || item.pricing?.originalPrice || item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0)
                                            : (item.selectedWeight?.discountPrice || item.pricing?.discountPrice || item.selectedWeight?.originalPrice || item.pricing?.originalPrice || 0);
                                        return sum + (price * (item.quantity || 1));
                                    }, 0);

                                    let activeItemsCount = 0;
                                    let originalTotal = 0;
                                    let discountedTotal = 0;
                                    let productDiscount = 0;
                                    let activeTotal = 0;
                                    let couponDiscount = 0;
                                    let freeCashDiscount = 0;
                                    let deliveryCharges = 0;
                                    let itemsForGst = [];

                                    const needsBillingFromItems = (orderDetails.hasPartialReturn || orderDetails.hasPartialCancellation)
                                        || allItems.some((i) => i.isReturned || (i.returnedQuantity || 0) > 0);

                                    if (!isOrderCancelled && billSummary) {
                                        activeItemsCount = billSummary.itemUnitCount ?? billSummary.itemLineCount ?? 0;
                                        originalTotal = billSummary.originalTotal ?? 0;
                                        productDiscount = billSummary.productDiscount ?? 0;
                                        discountedTotal = billSummary.discountedSubtotal ?? 0;
                                        couponDiscount = billSummary.couponDiscount ?? 0;
                                        freeCashDiscount = billSummary.freeCashDiscount ?? 0;
                                        deliveryCharges = billSummary.deliveryCharges ?? 0;
                                        activeTotal = billSummary.finalAmount ?? 0;
                                        itemsForGst = getBillableItems(allItems);
                                    } else if (!isOrderCancelled && needsBillingFromItems && orderDetails.financial?.finalAmount != null) {
                                        const activeItems = getBillableItems(allItems);
                                        itemsForGst = activeItems;
                                        activeItemsCount = activeItems.reduce((s, i) => s + (i.quantity || 1), 0);
                                        originalTotal = orderDetails.financial.totalOriginalPrice ?? orderDetails.financial.totalMrp ?? sumFromItems(activeItems, 'original');
                                        discountedTotal = orderDetails.financial.subtotal ?? sumFromItems(activeItems, 'discount');
                                        productDiscount = originalTotal - discountedTotal;
                                        if (productDiscount < 0) productDiscount = orderDetails.financial.totalDiscount - (orderDetails.financial.couponDiscount || 0);
                                        couponDiscount = orderDetails.financial.couponDiscount || 0;
                                        freeCashDiscount = orderDetails.financial.freeCashDiscount || 0;
                                        deliveryCharges = orderDetails.financial.deliveryCharges || 0;
                                        activeTotal = orderDetails.financial.finalAmount;
                                    } else if (isOrderCancelled) {
                                        originalTotal = sumFromItems(allItems, 'original');
                                        discountedTotal = sumFromItems(allItems, 'discount');
                                        productDiscount = originalTotal - discountedTotal;
                                        couponDiscount = orderDetails.financial?.couponDiscount || 0;
                                        freeCashDiscount = orderDetails.financial?.freeCashDiscount || 0;
                                        deliveryCharges = orderDetails.financial?.deliveryCharges || 0;
                                        activeTotal = discountedTotal - couponDiscount - freeCashDiscount + deliveryCharges;
                                        itemsForGst = allItems;
                                        activeItemsCount = allItems.reduce((s, i) => s + (i.quantity || 1), 0);
                                    } else {
                                        const activeItems = getBillableItems(allItems);
                                        itemsForGst = activeItems;
                                        activeItemsCount = activeItems.reduce((s, i) => s + (i.quantity || 1), 0);
                                        originalTotal = sumFromItems(activeItems, 'original');
                                        discountedTotal = sumFromItems(activeItems, 'discount');
                                        productDiscount = originalTotal - discountedTotal;
                                        couponDiscount = orderDetails.financial?.couponDiscount || 0;
                                        freeCashDiscount = orderDetails.financial?.freeCashDiscount || 0;
                                        deliveryCharges = orderDetails.financial?.deliveryCharges || 0;
                                        activeTotal = orderDetails.financial?.finalAmount
                                            ?? (discountedTotal - couponDiscount - freeCashDiscount + deliveryCharges);
                                    }
                                    
                                    return (
                                        <>
                                            <div className="flex justify-between text-gray-700">
                                                <div className="flex items-center gap-2">
                                                    <span>
                                                        Price ({activeItemsCount} {activeItemsCount === 1 ? 'item' : 'items'})
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="text-blue-500 hover:text-blue-700 text-xs underline focus:outline-none"
                                                        onClick={() => setShowGstDetails(!showGstDetails)}
                                                    >
                                                        {showGstDetails ? 'Hide tax info' : 'Show tax info'}
                                                    </button>
                                                </div>
                                                <span className="font-semibold">
                                                    ₹{originalTotal.toFixed(2)}
                                                </span>
                                            </div>

                                            {/* GST Breakdown */}
                                            {showGstDetails && (
                                                <div className="rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 p-3 my-2 border border-blue-100 flex flex-col gap-1 animate-fadeIn">
                                                    {itemsForGst.map(item => {
                                                        const isExclusive = item.gstDetails?.gstType === "exclusive";
                                                        return (
                                                            <div key={item._id || item.productId} className="flex justify-between text-xs text-gray-500">
                                                                <span className={isExclusive ? "text-blue-600 font-semibold" : ""}>
                                                                    {item.productName} ({isExclusive ? "Exclusive" : "Inclusive"})
                                                                </span>
                                                                <span className={isExclusive ? "text-blue-600 font-semibold" : ""}>
                                                                    ₹{(item.gstDetails?.totalGstAmount || 0).toFixed(2)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Discount */}
                                            <div className="flex justify-between text-gray-700">
                                                <span>Discount</span>
                                                <span className="font-semibold text-green-600">
                                                    -₹{productDiscount.toFixed(2)}
                                                </span>
                                            </div>

                                            <div className="flex justify-between text-gray-700">
                                                <span>
                                                    Price After Discount
                                                </span>
                                                <span className="font-semibold">
                                                    ₹{discountedTotal.toFixed(2)}
                                                </span>
                                            </div>

                                            {/* Coupon Discount */}
                                            {couponDiscount > 0 && (
                                                <div className="flex justify-between text-gray-700">
                                                    <span>Coupon</span>
                                                    <span className="font-semibold text-green-600">
                                                        -₹{couponDiscount.toFixed(2)}
                                                    </span>
                                                </div>
                                            )}

                                            <FreeCashBillingLine
                                                amount={freeCashDiscount}
                                                className="flex justify-between text-gray-700"
                                                valueClass="font-semibold text-teal-600"
                                            />

                                            {/* Delivery Charges */}
                                            <div className="flex justify-between text-gray-700">
                                                <span>Delivery Charges</span>
                                                <span className="font-semibold text-blue-600">
                                                    ₹{deliveryCharges.toFixed(2)}
                                                </span>
                                            </div>

                                            <hr className="border-t-2 border-dashed border-gray-300 my-4" />

                                            {/* Total */}
                                            <div className="flex justify-between text-xl font-bold text-gray-900">
                                                <span>Total</span>
                                                <span className="text-blue-600">₹{activeTotal.toFixed(2)}</span>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                            <div className="mt-6 flex flex-row items-center justify-center gap-4">
                                <button
                                    onClick={handlePrintThermalBill}
                                    className="flex items-center gap-2 w-[50%] justify-center bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg transition-all duration-300"
                                >
                                    <FiPrinter className="text-lg" />
                                    Print Bill
                                </button>
                            </div>
                        </motion.div>

                        {/* Shiprocket Shipping Details */}
                        {orderDetails?.delivery?.deliveryOption?.source === 'shiprocket' && (
                            <motion.div
                                className={`bg-white rounded-xl shadow-lg p-6 border ${orderDetails?.delivery?.deliveryOption?.isHyperlocal ? 'border-green-200' : 'border-orange-100'} hover:shadow-xl transition-shadow duration-300`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.15 }}
                            >
                                <div className="flex items-center gap-2 mb-4">
                                    <span className={`text-xl ${orderDetails?.delivery?.deliveryOption?.isHyperlocal ? 'text-green-500' : 'text-orange-500'}`}>
                                        {orderDetails?.delivery?.deliveryOption?.isHyperlocal ? '⚡' : '🚀'}
                                    </span>
                                    <h2 className="text-xl font-bold text-gray-800">
                                        {orderDetails?.delivery?.deliveryOption?.isHyperlocal ? 'Hyperlocal Delivery' : 'Shiprocket Delivery'}
                                    </h2>
                                    {orderDetails?.delivery?.deliveryOption?.isHyperlocal && (
                                        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold">SAME DAY</span>
                                    )}
                                </div>

                                {/* Delivery Option Info */}
                                <div className="space-y-3 mb-4">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Courier</span>
                                        <span className="font-medium text-gray-800">
                                            {orderDetails.shiprocket?.courierName || orderDetails.delivery?.deliveryOption?.service || orderDetails.delivery?.deliveryOption?.name || 'Shiprocket'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Delivery Type</span>
                                        <span className="font-medium text-gray-800">
                                            {orderDetails.delivery?.deliveryOption?.type || 'Standard'}
                                        </span>
                                    </div>
                                    {orderDetails.delivery?.deliveryOption?.estimatedTime && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-500">Est. Delivery</span>
                                            <span className="font-medium text-gray-800">
                                                {orderDetails.delivery.deliveryOption.estimatedTime}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Shipping Charges</span>
                                        <span className="font-bold text-blue-600">
                                            ₹{(orderDetails.financial?.deliveryCharges || orderDetails.delivery?.deliveryOption?.price || 0).toFixed(2)}
                                        </span>
                                    </div>
                                </div>

                                {/* Shiprocket Order/Shipment Info */}
                                {orderDetails.shiprocket && (
                                    <>
                                        <hr className="border-dashed border-gray-200 my-3" />
                                        <div className="space-y-3">
                                            {orderDetails.shiprocket.shiprocketOrderId && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500">SR Order ID</span>
                                                    <span className="font-mono text-gray-800">{orderDetails.shiprocket.shiprocketOrderId}</span>
                                                </div>
                                            )}
                                            {orderDetails.shiprocket.shipmentId && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500">Shipment ID</span>
                                                    <span className="font-mono text-gray-800">{orderDetails.shiprocket.shipmentId}</span>
                                                </div>
                                            )}
                                            {orderDetails.shiprocket.awbCode && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500">AWB / Tracking</span>
                                                    <span className="font-mono font-bold text-indigo-600">{orderDetails.shiprocket.awbCode}</span>
                                                </div>
                                            )}
                                            {orderDetails.shiprocket.courierName && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500">Courier Partner</span>
                                                    <span className="font-medium text-gray-800">{orderDetails.shiprocket.courierName}</span>
                                                </div>
                                            )}
                                            {(() => {
                                                // Map Shiprocket status to our order status labels
                                                const srStatus = orderDetails.shiprocket.currentStatus;
                                                const rawSrStatusText = orderDetails.shiprocket.currentStatusText;
                                                const srStatusText = typeof rawSrStatusText === 'string' ? rawSrStatusText : '';
                                                let statusLabel = srStatusText || String(orderDetails.shiprocket.status || '');
                                                let statusStyle = 'bg-blue-100 text-blue-700';
                                                if (srStatus == 6 || (srStatusText || '').toLowerCase().includes('delivered')) {
                                                    statusLabel = 'Delivered';
                                                    statusStyle = 'bg-green-100 text-green-700';
                                                } else if (srStatus == 17 || srStatus == 18 || (srStatusText || '').toLowerCase().includes('out for delivery')) {
                                                    statusLabel = 'Out For Delivery';
                                                    statusStyle = 'bg-orange-100 text-orange-700';
                                                } else if ((srStatus >= 2 && srStatus <= 5) || (srStatusText || '').toLowerCase().includes('transit') || (srStatusText || '').toLowerCase().includes('picked')) {
                                                    statusLabel = 'Shipped';
                                                    statusStyle = 'bg-purple-100 text-purple-700';
                                                } else if (srStatus == 7 || srStatus == 8 || (srStatusText || '').toLowerCase().includes('cancel') || (srStatusText || '').toLowerCase().includes('rto')) {
                                                    statusLabel = srStatusText || 'Cancelled / RTO';
                                                    statusStyle = 'bg-red-100 text-red-700';
                                                } else if (srStatus == 1 || (statusLabel || '').toUpperCase() === 'NEW') {
                                                    statusLabel = 'Ready to Ship';
                                                    statusStyle = 'bg-indigo-100 text-indigo-700';
                                                }
                                                return statusLabel ? (
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-500">Shiprocket Status</span>
                                                        <span className={`px-2 py-1 ${statusStyle} rounded-full text-xs font-medium`}>
                                                            {statusLabel}
                                                        </span>
                                                    </div>
                                                ) : null;
                                            })()}
                                            {orderDetails.shiprocket.etd && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500">Expected Delivery</span>
                                                    <span className="font-medium text-green-600">{formatShiprocketDateTime(orderDetails.shiprocket.etd)}</span>
                                                </div>
                                            )}
                                            {orderDetails.shiprocket.pickupScheduled && (
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-500">Pickup</span>
                                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                                        Scheduled ✓
                                                    </span>
                                                </div>
                                            )}
                                            {orderDetails.shiprocket.labelUrl && (
                                                <div className="flex justify-between text-sm items-center">
                                                    <span className="text-gray-500">Shipping Label</span>
                                                    <a
                                                        href={orderDetails.shiprocket.labelUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors"
                                                    >
                                                        Download Label
                                                    </a>
                                                </div>
                                            )}
                                            {orderDetails.shiprocket.cancelled && (
                                                <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                                                    <p className="text-sm text-red-600 font-medium">Shiprocket shipment cancelled</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Tracking Activities Timeline */}
                                        {orderDetails.shiprocket.trackingActivities && orderDetails.shiprocket.trackingActivities.length > 0 && (
                                            <>
                                                <hr className="border-dashed border-gray-200 my-3" />
                                                <h3 className="text-sm font-semibold text-gray-700 mb-3">Activity Log</h3>
                                                <div className="space-y-3 max-h-60 overflow-y-auto">
                                                    {orderDetails.shiprocket.trackingActivities.map((activity, idx) => (
                                                        <div key={idx} className="flex gap-3 text-xs">
                                                            <div className="flex flex-col items-center">
                                                                <div className={`w-2.5 h-2.5 rounded-full ${idx === 0 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                                                {idx < orderDetails.shiprocket.trackingActivities.length - 1 && (
                                                                    <div className="w-0.5 h-8 bg-gray-200"></div>
                                                                )}
                                                            </div>
                                                            <div className="flex-1 pb-2">
                                                                <p className="text-gray-800 font-medium">{activity['sr-status-label'] || activity.activity || activity.status}</p>
                                                                <p className="text-gray-400">{formatShiprocketDateTime(activity.date || activity.timestamp)}</p>
                                                                {/* {activity.location && <p className="text-gray-500">{activity.location}</p>} */}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                {/* Action: Create Shipment if not created yet */}
                                {!orderDetails.shiprocket?.shiprocketOrderId && orderDetails.status === 'Confirmed' && (
                                    <div className="mt-4">
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const { createShiprocketShipment } = await import("../../services/ClientService");
                                                    const res = await createShiprocketShipment({ orderId: orderDetails._id });
                                                    if (res?.success) {
                                                        toast.success("Shiprocket shipment created!");
                                                        fetchOrderDetails();
                                                    } else {
                                                        toast.error(res?.message || "Failed to create shipment");
                                                    }
                                                } catch (err) {
                                                    toast.error("Failed to create shipment");
                                                }
                                            }}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium text-sm"
                                        >
                                            🚀 Create Shiprocket Shipment
                                        </button>
                                    </div>
                                )}

                                {/* Action: Ship Now — Open courier selection modal (or auto-assign for hyperlocal) */}
                                {orderDetails.shiprocket?.shiprocketOrderId && !orderDetails.shiprocket?.cancelled && !orderDetails.shiprocket?.awbCode && (
                                    <div className="mt-4">
                                        {orderDetails?.delivery?.deliveryOption?.isHyperlocal ? (
                                            /* Hyperlocal: Skip courier selection — Shiprocket auto-assigns */
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const { assignShiprocketAWB, scheduleShiprocketPickup, generateShiprocketLabel } = await import("../../services/ClientService");
                                                        toast.info("Assigning hyperlocal courier...");
                                                        const awbRes = await assignShiprocketAWB({
                                                            orderId: orderDetails._id,
                                                            courierId: null, // No courier_id for hyperlocal — Shiprocket handles it
                                                            isHyperlocal: true,
                                                        });
                                                        if (!awbRes?.success) {
                                                            toast.error(awbRes?.message || "Failed to assign AWB");
                                                            return;
                                                        }
                                                        toast.success("AWB assigned!");

                                                        try {
                                                            await scheduleShiprocketPickup({ orderId: orderDetails._id });
                                                            toast.success("Pickup scheduled!");
                                                        } catch (e) {
                                                            console.warn("Pickup scheduling skipped:", e?.message);
                                                        }

                                                        try {
                                                            await generateShiprocketLabel({ orderId: orderDetails._id });
                                                            toast.success("Label generated!");
                                                        } catch (e) {
                                                            console.warn("Label generation skipped:", e?.message);
                                                        }

                                                        fetchOrderDetails();
                                                    } catch (err) {
                                                        toast.error(err?.message || "Failed to ship");
                                                    }
                                                }}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                                            >
                                                ⚡ Ship Now (Hyperlocal)
                                            </button>
                                        ) : (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    setShowCourierModal(true);
                                                    setCourierModalLoading(true);
                                                    setCourierModalError('');
                                                    setCourierList([]);
                                                    setCourierOrderInfo(null);
                                                    setCustomerSelectedCourier(null);
                                                    const { getShiprocketOrderCourierRates } = await import("../../services/ClientService");
                                                    const res = await getShiprocketOrderCourierRates({ orderId: orderDetails._id });
                                                    if (res?.success && res?.data?.couriers) {
                                                        setCourierList(res.data.couriers);
                                                        setCourierOrderInfo(res.data.orderInfo);
                                                        setRecommendedCourierId(res.data.recommendedCourierId || res.data.shiprocketRecommendedId || null);
                                                        setCustomerSelectedCourier(res.data.customerSelectedCourier || null);
                                                        setCourierFilter('all');
                                                    } else {
                                                        setCourierModalError(res?.message || "No couriers available");
                                                    }
                                                } catch (err) {
                                                    setCourierModalError(err?.message || "Failed to fetch courier rates");
                                                } finally {
                                                    setCourierModalLoading(false);
                                                }
                                            }}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                                        >
                                            📦 Ship Now
                                        </button>
                                        )}
                                    </div>
                                )}

                                {/* Action: Cancel Shiprocket Order */}
                                {orderDetails.shiprocket?.shiprocketOrderId && !orderDetails.shiprocket?.cancelled && !orderDetails.shiprocket?.delivered && (
                                    <div className="mt-3">
                                        <button
                                            onClick={() => setShowShiprocketCancelConfirm(true)}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors font-medium text-sm"
                                        >
                                            ✕ Cancel Shiprocket Order
                                        </button>
                                    </div>
                                )}

                                {/* Action: Create Ticket (Shiprocket portal deep-link) */}
                                {orderDetails.shiprocket?.shiprocketOrderId
                                    && !orderDetails.shiprocket?.cancelled
                                    && orderDetails.status !== 'Delivered'
                                    && !orderDetails.shiprocket?.delivered
                                    && !String(orderDetails.shiprocket?.currentStatusText || '').toLowerCase().includes('delivered') && (
                                    <div className="mt-3">
                                        <button
                                            onClick={handleCreateShiprocketTicket}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors font-medium text-sm"
                                        >
                                            📝 Create Ticket
                                        </button>
                                    </div>
                                )}

                                {/* Action: Create Return */}
                                {orderDetails.shiprocket?.shiprocketOrderId && (orderDetails.status === 'Delivered' || orderDetails.shiprocket?.delivered) && !orderDetails.shiprocket?.returnOrderId && (
                                    <div className="mt-3">
                                        <button
                                            onClick={handleCreateShiprocketReturn}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors font-medium text-sm"
                                        >
                                            ↩ Create Return
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </div>
                </div>
            </div >

            {/* Delivery Details Modal */}
            {
                showDeliveryModal && (
                    <>
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 relative m-2 h-[90vh] flex flex-col">
                            <button
                                onClick={() => setShowDeliveryModal(false)}
                                className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
                                aria-label="Close"
                                type="button"
                            >
                                &times;
                            </button>
                            <div className="flex items-center gap-2 mb-3">
                                <FiShare2 className="text-2xl text-orange-500" />
                                <h2 className="text-lg font-bold text-gray-800">Delivery Details</h2>
                            </div>

                            <div className="scrollbar-thin overflow-y-auto flex-1 pr-2">
                                {/* Checkbox for including item and bill details */}
                                <div className="mb-4 flex items-center gap-2">
                                    <input
                                        id="include-details"
                                        type="checkbox"
                                        checked={includeDetails}
                                        onChange={() => setIncludeDetails((v) => !v)}
                                        className="accent-blue-600 w-4 h-4 rounded border-gray-300"
                                    />
                                    <label htmlFor="include-details" className="text-sm text-gray-700 select-none cursor-pointer">
                                        Include Items and Bill Details
                                    </label>
                                </div>
                                
                                {/* Primary Delivery Information */}
                                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FiMapPin className="text-blue-600" />
                                        <h3 className="text-sm font-bold text-blue-900">Delivery Information</h3>
                                    </div>
                                    <div className="space-y-1 text-gray-700 text-sm">
                                        <div>
                                            <span className="font-semibold">Order ID:</span> {orderDetails.saleId || orderDetails.orderId}
                                        </div>
                                        <div>
                                            <span className="font-semibold">Name:</span> {orderDetails?.addressInfo?.name || "N/A"}
                                        </div>
                                        <div>
                                            <span className="font-semibold">Mobile:</span> {orderDetails?.addressInfo?.phone || "N/A"}
                                        </div>
                                        <div>
                                            <span className="font-semibold">Delivery Address:</span> {getFullAddress(orderDetails?.addressInfo)}
                                            {orderDetails?.addressInfo && (
                                                <a
                                                    href={getGoogleMapsLink()}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="ml-2 inline-flex items-center text-blue-600 hover:text-blue-800 underline"
                                                    title="Navigate in Google Maps"
                                                >
                                                    <FiMapPin className="mr-1" /> Maps
                                                </a>
                                            )}
                                        </div>
                                        <div>
                                            <span className="font-semibold">Delivery Type:</span>{" "}
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {orderDetails?.delivery?.deliveryOption?.type || orderDetails?.delivery?.deliveryOption?.name || "Standard"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="font-semibold">Payment Type:</span>{" "}
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                {getPaymentMethodLabel(orderDetails?.payment?.paymentMode || orderDetails?.payment?.method || orderDetails?.payment?.paymentType)}
                                            </span>
                                        </div>
                                        {orderDetails?.addressInfo?.directions && (
                                            <div>
                                                <span className="font-semibold">Directions:</span> {orderDetails?.addressInfo?.directions}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Secondary Customer Information (Optional) */}
                                {(orderDetails?.customer?.customerName || orderDetails?.customer?.customerPhone) && (
                                    <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                        <div className="flex items-center gap-2 mb-2">
                                            <AiOutlineUser className="text-gray-600" />
                                            <h3 className="text-sm font-bold text-gray-700">Customer Details (Secondary Details)</h3>
                                        </div>
                                        <div className="space-y-1 text-gray-600 text-sm">
                                            {orderDetails?.customer?.customerName && (
                                                <div>
                                                    <span className="font-semibold">Customer Name:</span> {orderDetails?.customer?.customerName}
                                                </div>
                                            )}
                                            {orderDetails?.customer?.customerPhone && (
                                                <div>
                                                    <span className="font-semibold">Customer Phone:</span> {orderDetails?.customer?.customerPhone}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Items and Bill Details */}
                                {includeDetails && (
                                    <div className="space-y-1 text-gray-700 text-sm">
                                        <div>
                                            <span className="font-semibold">Items:</span>
                                            <ul className="list-disc list-inside mt-1 ml-2 text-gray-600">
                                                {orderDetails.productsInfo.filter(item => !item.isCancelled).map((item, idx) => (
                                                    <li key={item.productId}>
                                                        <span className="font-medium">{item.productName}</span>
                                                        <span className="ml-1 text-xs text-gray-500">
                                                            ({typeof item.selectedWeight.weight === 'object' && item.selectedWeight.weight !== null
                                                                ? item.selectedWeight.weight.weightLabel || `${item.selectedWeight.weight.weightValue} ${item.selectedWeight.weight.metricLabel}`
                                                                : item.selectedWeight.weight})
                                                        </span>
                                                        <span className="ml-2">x{item.quantity}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <span className="font-semibold">Total Bill:</span> ₹{calculateOrderTotal()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                                <a
                                    href={getWhatsAppShareLink()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-2 rounded bg-green-500 hover:bg-green-600 text-white text-sm font-semibold"
                                    title="Send via WhatsApp"
                                >
                                    <FaWhatsapp />
                                    WhatsApp
                                </a>
                                <a
                                    href={getSMSShareLink()}
                                    className="flex items-center gap-2 px-3 py-2 rounded bg-purple-500 hover:bg-purple-600 text-white text-sm font-semibold"
                                    title="Send via SMS"
                                >
                                    <FiMessageCircle />
                                    SMS
                                </a>
                                <button
                                    onClick={handleCopyDeliveryDetails}
                                    className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-semibold ${copied
                                        ? "bg-green-500 text-white"
                                        : "bg-blue-500 hover:bg-blue-600 text-white"
                                        }`}
                                    title="Copy details"
                                    type="button"
                                >
                                    {copied ? <FiCheck /> : <FiShare2 />}
                                    {copied ? "Copied!" : "Copy"}
                                </button>
                                <button
                                    onClick={() => setShowDeliveryModal(false)}
                                    className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold"
                                    type="button"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                    </>
                )
            }



            {/* Status Change Modal */}
            {
                showStatusModal && (
                    <StatusChangeModal
                        showStatusModal={showStatusModal}
                        setShowStatusModal={setShowStatusModal}
                        selectedStatus={selectedStatus}
                        setSelectedStatus={setSelectedStatus}
                        handleChangeStatus={handleChangeStatus}
                        statusFlow={(isMarketplaceOrder || orderDetails?.isMarketplaceOrder) ? MARKETPLACE_STATUS_FLOW : STATUS_FLOW}
                    />
                )
            }

            {/* Assign Driver Modal */}
            {showAssignDriverModal && orderDetails && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAssignDriverModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-lg font-semibold text-gray-900 mb-1">Assign Delivery Driver</h2>
                        <p className="text-sm text-gray-500 mb-4">Order: {orderDetails.saleId || orderDetails.orderId || orderDetails._id}</p>

                        {loadingDrivers ? (
                            <p className="text-center py-4 text-gray-500">Loading available drivers...</p>
                        ) : availableDrivers.length === 0 ? (
                            <p className="text-center py-4 text-gray-500">No available drivers found. Add drivers from Delivery Controls.</p>
                        ) : (
                            <div className="space-y-3">
                                <label className="block text-sm font-medium text-gray-700">Select Driver</label>
                                <select
                                    value={selectedDriverId}
                                    onChange={(e) => setSelectedDriverId(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                >
                                    <option value="">-- Choose a driver --</option>
                                    {availableDrivers.map((d) => (
                                        <option key={d._id} value={d._id}>
                                            {d.name} — {d.vehicleType} ({d.activeOrderCount || 0} active)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowAssignDriverModal(false)}
                                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAssignDriver}
                                disabled={!selectedDriverId}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                            >
                                Assign Driver
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transfer Order Modal — original */}
            {showTransferModal && orderDetails && (
                <TransferOrderModal
                    isOpen={showTransferModal}
                    onClose={() => setShowTransferModal(false)}
                    order={getOrderForTransfer()}
                    branches={[]}
                    currentBranchId={orderDetails.branch?.branchId}
                    onTransfer={handleTransferComplete}
                />
            )}

            <ReturnRestoreModal
                isOpen={showReturnRequestModal}
                onClose={() => setShowReturnRequestModal(false)}
                orderDetails={orderDetails}
                returnRequests={returnRequests}
                mode="request"
                onSuccess={async () => {
                    await fetchOrderDetails();
                    try {
                        const timelineResponse = await getOrderTimeline({ orderId: id });
                        if (timelineResponse?.success) {
                            setTimeline(timelineResponse.data?.timeline || []);
                            setReturnRequests(timelineResponse.data?.returnRequests || []);
                        }
                    } catch (e) {
                        console.error('Error refreshing timeline after return:', e);
                    }
                }}
            />

            <ReturnRestoreModal
                isOpen={showReturnRestoreModal}
                onClose={() => setShowReturnRestoreModal(false)}
                orderDetails={orderDetails}
                returnRequests={returnRequests}
                mode="restore"
                onSuccess={async () => {
                    await fetchOrderDetails();
                    try {
                        const timelineResponse = await getOrderTimeline({ orderId: id });
                        if (timelineResponse?.success) {
                            setTimeline(timelineResponse.data?.timeline || []);
                            setReturnRequests(timelineResponse.data?.returnRequests || []);
                        }
                    } catch (e) {
                        console.error('Error refreshing timeline after return:', e);
                    }
                }}
            />

            {/* Item Status Change Modal */}
            {showItemStatusModal && selectedItemForStatus && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl w-full max-w-md h-[80vh] shadow-2xl overflow-hidden flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Change Item Status</h3>
                                    <p className="text-xs text-gray-500 mt-0.5">Update status for individual item</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowItemStatusModal(false);
                                    setSelectedItemForStatus(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        {/* Content */}
                        <div className="p-5 overflow-y-auto flex-1">
                            {/* Product Info */}
                            <div className="bg-gray-50 rounded-xl p-4 mb-4">
                                <p className="font-semibold text-gray-900">{selectedItemForStatus.productName}</p>
                                <p className="text-sm text-gray-500 mt-1">
                                    {typeof selectedItemForStatus.selectedWeight?.weight === 'object'
                                        ? selectedItemForStatus.selectedWeight.weight.weightLabel
                                        : selectedItemForStatus.selectedWeight?.weight || 'N/A'}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="text-xs text-gray-500">Current Status:</span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getItemStatusColor(selectedItemForStatus.itemStatus || orderDetails.status).bg} ${getItemStatusColor(selectedItemForStatus.itemStatus || orderDetails.status).text}`}>
                                        {selectedItemForStatus.itemStatus || orderDetails.status}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Status Options */}
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-gray-700 mb-3">Select New Status:</p>
                                {STATUS_FLOW.filter(status => status !== 'Cancelled').map((status) => {
                                    const currentStatus = selectedItemForStatus.itemStatus || orderDetails.status;
                                    const isCurrentStatus = status === currentStatus;
                                    const colors = getItemStatusColor(status);
                                    
                                    return (
                                        <button
                                            key={status}
                                            onClick={() => !isCurrentStatus && handleUpdateItemStatus(status)}
                                            disabled={isCurrentStatus || updatingItemStatus}
                                            className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                                                isCurrentStatus 
                                                    ? 'border-green-300 bg-green-50 cursor-not-allowed'
                                                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className={`w-3 h-3 rounded-full ${colors.bg.replace('100', '500')}`}></span>
                                                <span className={`font-medium ${isCurrentStatus ? 'text-green-700' : 'text-gray-700'}`}>{status}</span>
                                            </div>
                                            {isCurrentStatus && (
                                                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            
                            {/* Note about global status */}
                            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-xs text-amber-700">
                                        When all items reach the same status, the order's global status will automatically update to match.
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Footer */}
                        <div className="p-5 border-t border-gray-100 flex justify-end">
                            <button
                                onClick={() => {
                                    setShowItemStatusModal(false);
                                    setSelectedItemForStatus(null);
                                }}
                                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Cancel Item Modal */}
            {showCancelItemModal && selectedCancelItem && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
                    >
                        {/* Header */}
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                    <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Cancel Item</h3>
                            </div>
                            {!cancellingItem && (
                                <button
                                    onClick={() => {
                                        setShowCancelItemModal(false);
                                        setSelectedCancelItem(null);
                                        setItemCancellationReason('');
                                    }}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                                >
                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Content */}
                        <div className="p-5">
                            <p className="text-sm text-gray-600 mb-4">
                                Are you sure you want to cancel this item? The order total will be recalculated and inventory will be restored.
                            </p>

                            {/* Item Info */}
                            <div className="p-4 bg-gray-50 rounded-xl mb-4">
                                <p className="font-semibold text-gray-900">{selectedCancelItem.productName}</p>
                                <p className="text-sm text-gray-500 mt-1">
                                    Qty: {selectedCancelItem.quantity} × ₹{(selectedCancelItem.selectedWeight?.discountPrice || selectedCancelItem.pricing?.discountPrice || 0).toFixed(2)}
                                </p>
                                <p className="text-sm font-medium text-gray-700 mt-1">
                                    Total: ₹{((selectedCancelItem.selectedWeight?.discountPrice || selectedCancelItem.pricing?.discountPrice || 0) * selectedCancelItem.quantity).toFixed(2)}
                                </p>
                            </div>

                            {/* Reason Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason for cancellation (optional)
                                </label>
                                <textarea
                                    value={itemCancellationReason}
                                    onChange={(e) => setItemCancellationReason(e.target.value)}
                                    placeholder="e.g., Out of stock, Wrong item ordered..."
                                    disabled={cancellingItem}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 p-5 pt-0">
                            <button
                                onClick={() => {
                                    setShowCancelItemModal(false);
                                    setSelectedCancelItem(null);
                                    setItemCancellationReason('');
                                }}
                                disabled={cancellingItem}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold transition-colors"
                            >
                                Keep Item
                            </button>
                            <button
                                onClick={handleCancelItem}
                                disabled={cancellingItem}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                            >
                                {cancellingItem ? (
                                    <>
                                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Cancelling...
                                    </>
                                ) : (
                                    'Cancel Item'
                                )}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* ===== Post-Order: Exception Modal ===== */}
            {showExceptionModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
                    >
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                    <FaExclamationTriangle className="w-5 h-5 text-red-600" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Flag Exception</h3>
                            </div>
                            <button
                                onClick={() => { setShowExceptionModal(false); setExceptionType(''); setExceptionDescription(''); setExceptionSeverity('medium'); }}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                            >
                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Exception Type</label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    options={[
                                        { value: '', label: 'Select type...' },
                                        { value: 'delivery_delay', label: 'Delivery Delay' },
                                        { value: 'wrong_item', label: 'Wrong Item' },
                                        { value: 'damaged_item', label: 'Damaged Item' },
                                        { value: 'missing_item', label: 'Missing Item' },
                                        { value: 'quality_issue', label: 'Quality Issue' },
                                        { value: 'customer_complaint', label: 'Customer Complaint' },
                                        { value: 'address_issue', label: 'Address Issue' },
                                        { value: 'payment_issue', label: 'Payment Issue' },
                                        { value: 'other', label: 'Other' },
                                    ].filter(o => o.value !== '' )}
                                    placeholder="Select type..."
                                    isClearable
                                    value={exceptionType ? { value: exceptionType, label: (exceptionType || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) } : null}
                                    onChange={(opt) => setExceptionType(opt ? opt.value : '')}
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                                <div className="flex gap-2">
                                    {['low', 'medium', 'high', 'critical'].map(sev => (
                                        <button
                                            key={sev}
                                            onClick={() => setExceptionSeverity(sev)}
                                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold capitalize transition-all ${
                                                exceptionSeverity === sev
                                                    ? sev === 'low' ? 'bg-blue-500 text-white'
                                                    : sev === 'medium' ? 'bg-yellow-500 text-white'
                                                    : sev === 'high' ? 'bg-orange-500 text-white'
                                                    : 'bg-red-600 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            {sev}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                <textarea
                                    value={exceptionDescription}
                                    onChange={(e) => setExceptionDescription(e.target.value)}
                                    placeholder="Describe the issue in detail..."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 p-5 pt-0">
                            <button
                                onClick={() => { setShowExceptionModal(false); setExceptionType(''); setExceptionDescription(''); setExceptionSeverity('medium'); }}
                                disabled={flaggingException}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleFlagException}
                                disabled={flaggingException || !exceptionType || !exceptionDescription}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {flaggingException ? (
                                    <>
                                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                                        Flagging...
                                    </>
                                ) : 'Flag Exception'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* ===== Post-Order: Add Note Modal ===== */}
            {showNoteModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
                    >
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                    <FiMessageCircle className="w-5 h-5 text-amber-600" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Add Internal Note</h3>
                            </div>
                            <button
                                onClick={() => { setShowNoteModal(false); setNoteText(''); }}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                            >
                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="p-5">
                            <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="Add a note about this order (visible to team only)..."
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                            />
                            <p className="text-xs text-gray-400 mt-2">Notes are internal and not visible to customers.</p>
                        </div>
                        <div className="flex gap-3 p-5 pt-0">
                            <button
                                onClick={() => { setShowNoteModal(false); setNoteText(''); }}
                                disabled={addingNote}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddNote}
                                disabled={addingNote || !noteText.trim()}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {addingNote ? (
                                    <>
                                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                                        Adding...
                                    </>
                                ) : 'Add Note'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* ===== Resolve Exception Modal ===== */}
            {showResolveModal && selectedExceptionToResolve && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowResolveModal(false)}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
                    >
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                    <FiCheck className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Resolve Exception</h3>
                                    <p className="text-xs text-gray-500 capitalize">{(selectedExceptionToResolve.data?.exceptionType || '').replace(/_/g, ' ')}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => { setShowResolveModal(false); setSelectedExceptionToResolve(null); setResolveNotes(''); }}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                            >
                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        {selectedExceptionToResolve.data?.description && (
                            <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                                <p className="text-xs text-red-600">{selectedExceptionToResolve.data.description}</p>
                            </div>
                        )}
                        <div className="p-5">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Notes</label>
                            <textarea
                                value={resolveNotes}
                                onChange={(e) => setResolveNotes(e.target.value)}
                                placeholder="Describe how this exception was resolved..."
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                            />
                        </div>
                        <div className="flex gap-3 p-5 pt-0">
                            <button
                                onClick={() => { setShowResolveModal(false); setSelectedExceptionToResolve(null); setResolveNotes(''); }}
                                disabled={resolvingException}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResolveException}
                                disabled={resolvingException || !resolveNotes.trim()}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {resolvingException ? (
                                    <>
                                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                                        Resolving...
                                    </>
                                ) : '✅ Mark as Resolved'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* ===== Post-Order: Timeline Side Panel ===== */}
            {showTimelinePanel && (
                <div className="fixed inset-0 z-40 flex justify-end" onClick={() => setShowTimelinePanel(false)}>
                    <div className="absolute inset-0 bg-black/30" />
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto"
                    >
                        <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Order Timeline</h3>
                                <p className="text-xs text-gray-500 mt-0.5">{timeline.length} events</p>
                            </div>
                            <button
                                onClick={() => setShowTimelinePanel(false)}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                            >
                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* Active Exceptions Banner */}
                        {activeExceptions.length > 0 && (
                            <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                                <p className="text-sm font-semibold text-red-700 mb-2">⚠ {activeExceptions.length} Active Exception{activeExceptions.length > 1 ? 's' : ''}</p>
                                {activeExceptions.map((exc, i) => (
                                    <div key={i} className="flex items-center justify-between mt-1.5 py-1.5 px-2 bg-white rounded-lg border border-red-100">
                                        <p className="text-xs text-red-600 flex-1">
                                            • {(exc.data?.exceptionType || exc.exceptionType)?.replace(/_/g, ' ')} — {(exc.data?.description || exc.description)?.substring(0, 50)}{(exc.data?.description || exc.description)?.length > 50 ? '...' : ''}
                                        </p>
                                        <button
                                            onClick={() => {
                                                setSelectedExceptionToResolve(exc);
                                                setResolveNotes('');
                                                setShowResolveModal(true);
                                            }}
                                            className="ml-2 px-2.5 py-1 text-[10px] font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md transition-colors whitespace-nowrap"
                                        >
                                            ✅ Resolve
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Timeline Events */}
                        <div className="p-5">
                            {timeline.length > 0 ? (
                                <div className="relative">
                                    <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-200" />
                                    <div className="space-y-6">
                                        {timeline.map((event, idx) => {
                                            const evType = event.eventType;
                                            const d = event.data || {};
                                            const isException = evType === 'exception_flagged';
                                            const isResolved = evType === 'exception_resolved';
                                            const isReturn = evType === 'return_requested';
                                            const isReturnChange = evType === 'return_status_change';
                                            const isNote = evType === 'note_added';
                                            const isQC = evType === 'qc_completed';
                                            const isRefundInit = evType === 'refund_initiated';
                                            const isRefundDone = evType === 'refund_completed';
                                            const isAccountability = evType === 'accountability_assigned';
                                            const isItemDecision = evType === 'item_decision';

                                            // Dot color based on event type
                                            const dotColor = isException ? 'bg-red-500' 
                                                : isResolved ? 'bg-green-500'
                                                : isReturn || isReturnChange ? 'bg-blue-500' 
                                                : isNote ? 'bg-amber-500' 
                                                : isQC ? (d.qcResult === 'passed' ? 'bg-emerald-500' : 'bg-red-500')
                                                : isRefundInit ? 'bg-orange-500'
                                                : isRefundDone ? 'bg-green-500'
                                                : isAccountability ? 'bg-purple-500'
                                                : 'bg-gray-500';

                                            // Card background based on type
                                            const cardBg = isException ? 'bg-red-50 border-red-100' 
                                                : isResolved ? 'bg-green-50 border-green-100'
                                                : isReturn || isReturnChange ? 'bg-blue-50 border-blue-100' 
                                                : isNote ? 'bg-amber-50 border-amber-100' 
                                                : isQC ? (d.qcResult === 'passed' ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100')
                                                : isRefundInit ? 'bg-orange-50 border-orange-100'
                                                : isRefundDone ? 'bg-green-50 border-green-100'
                                                : isAccountability ? 'bg-purple-50 border-purple-100'
                                                : 'bg-gray-50 border-gray-100';

                                            // Event icon
                                            const eventIcon = isException ? '🚨' 
                                                : isResolved ? '✅'
                                                : isReturn ? '🔄'
                                                : isReturnChange ? '🔁'
                                                : isNote ? '📝'
                                                : isQC ? (d.qcResult === 'passed' ? '✅' : '❌')
                                                : isRefundInit ? '💰'
                                                : isRefundDone ? '✅'
                                                : isAccountability ? '👤'
                                                : isItemDecision ? '📦'
                                                : '📋';

                                            // Event title
                                            const eventTitle = isException ? 'Exception Flagged'
                                                : isResolved ? 'Exception Resolved'
                                                : isReturn ? 'Return Requested'
                                                : isReturnChange ? `Return: ${d.statusFrom || '?'} → ${d.statusTo || '?'}`
                                                : isNote ? 'Note Added'
                                                : isQC ? `QC ${d.qcResult === 'passed' ? 'Passed' : 'Failed'}`
                                                : isRefundInit ? 'Refund Initiated'
                                                : isRefundDone ? 'Refund Completed'
                                                : isAccountability ? 'Accountability Assigned'
                                                : isItemDecision ? 'Item Decision'
                                                : evType?.replace(/_/g, ' ');

                                            // Format timestamp
                                            const ts = event.createdAt || event.timestamp;
                                            const formattedTime = ts ? new Date(ts).toLocaleString('en-IN', { 
                                                day: '2-digit', month: 'short', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit', hour12: true 
                                            }) : '';

                                            return (
                                                <div key={idx} className="relative pl-10">
                                                    <div className={`absolute left-2.5 top-1 w-3 h-3 rounded-full ${dotColor} ring-4 ring-white`} />
                                                    <div className={`p-3.5 rounded-xl border ${cardBg}`}>
                                                        {/* Header: Title + Timestamp */}
                                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                                            <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                                                                <span>{eventIcon}</span>
                                                                <span className="capitalize">{eventTitle}</span>
                                                            </p>
                                                            <p className="text-[10px] text-gray-400 whitespace-nowrap mt-0.5">{formattedTime}</p>
                                                        </div>

                                                        {/* === Exception Flagged === */}
                                                        {isException && (
                                                            <div className="space-y-1.5">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="text-xs font-medium text-gray-700 bg-white px-2 py-0.5 rounded border border-gray-200">
                                                                        {d.exceptionType?.replace(/_/g, ' ')}
                                                                    </span>
                                                                    {d.severity && (
                                                                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                                                            d.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                                                            d.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                                                                            d.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                                                            'bg-blue-100 text-blue-700'
                                                                        }`}>
                                                                            {d.severity}
                                                                        </span>
                                                                    )}
                                                                    {d.isResolved && (
                                                                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">
                                                                            ✅ Resolved
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {d.description && <p className="text-xs text-gray-600 leading-relaxed">{d.description}</p>}
                                                                {d.isResolved && d.resolvedNotes && (
                                                                    <div className="mt-1 p-2 bg-green-50 rounded-lg border border-green-200">
                                                                        <p className="text-[10px] font-semibold text-green-700 mb-0.5">Resolution Notes:</p>
                                                                        <p className="text-xs text-green-600">{d.resolvedNotes}</p>
                                                                        {d.resolvedBy && <p className="text-[10px] text-green-500 mt-1">Resolved by: {d.resolvedBy}{d.resolvedAt ? ` • ${new Date(d.resolvedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}` : ''}</p>}
                                                                    </div>
                                                                )}
                                                                {!d.isResolved && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setSelectedExceptionToResolve(event);
                                                                            setResolveNotes('');
                                                                            setShowResolveModal(true);
                                                                        }}
                                                                        className="mt-1.5 px-3 py-1 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md transition-colors"
                                                                    >
                                                                        ✅ Mark as Resolved
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* === Exception Resolved === */}
                                                        {isResolved && (
                                                            <div className="space-y-1.5">
                                                                {d.resolution && (
                                                                    <div className="flex items-center gap-1.5">
                                                                        <span className="text-xs text-gray-500">Resolution:</span>
                                                                        <span className="text-xs font-medium text-green-700">{d.resolution}</span>
                                                                    </div>
                                                                )}
                                                                {d.resolvedNotes && (
                                                                    <p className="text-xs text-gray-600 bg-white p-2 rounded-lg border border-green-200 leading-relaxed">{d.resolvedNotes}</p>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* === Return Requested === */}
                                                        {isReturn && (
                                                            <div className="space-y-1.5">
                                                                {d.returnRequestId && (
                                                                    <span className="text-xs font-mono font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                                                                        {d.returnRequestId}
                                                                    </span>
                                                                )}
                                                                <div className="grid grid-cols-2 gap-1.5 text-xs">
                                                                    {d.reason && (
                                                                        <div className="col-span-2">
                                                                            <span className="text-gray-500">Reason: </span>
                                                                            <span className="text-gray-700 font-medium">{d.reason}</span>
                                                                        </div>
                                                                    )}
                                                                    {d.itemCount && (
                                                                        <div>
                                                                            <span className="text-gray-500">Items: </span>
                                                                            <span className="text-gray-700 font-medium">{d.itemCount}</span>
                                                                        </div>
                                                                    )}
                                                                    {d.refundAmount != null && (
                                                                        <div>
                                                                            <span className="text-gray-500">Amount: </span>
                                                                            <span className="text-gray-700 font-semibold">₹{Number(d.refundAmount).toFixed(2)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {d.autoApproved && (
                                                                    <span className="text-[10px] font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                                                                        ⚡ Auto-approved
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* === Return Status Change === */}
                                                        {isReturnChange && (
                                                            <div className="space-y-1.5">
                                                                {d.returnRequestId && (
                                                                    <span className="text-xs font-mono font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                                                                        {d.returnRequestId}
                                                                    </span>
                                                                )}
                                                                <div className="flex items-center gap-2 text-xs">
                                                                    <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-600 font-medium">{d.statusFrom}</span>
                                                                    <span className="text-gray-400">→</span>
                                                                    <span className="px-2 py-0.5 rounded bg-blue-200 text-blue-700 font-semibold">{d.statusTo}</span>
                                                                </div>
                                                                {d.notes && <p className="text-xs text-gray-600 bg-white p-2 rounded-lg border border-gray-200 leading-relaxed">💬 {d.notes}</p>}
                                                            </div>
                                                        )}

                                                        {/* === QC Completed === */}
                                                        {isQC && (
                                                            <div className="space-y-1.5">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${d.qcResult === 'passed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                                        QC {d.qcResult === 'passed' ? '✅ Passed' : '❌ Failed'}
                                                                    </span>
                                                                    {d.qcDecision && (
                                                                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                                                            d.qcDecision === 'resale' ? 'bg-green-100 text-green-700' :
                                                                            d.qcDecision === 'refurbish' ? 'bg-amber-100 text-amber-700' :
                                                                            'bg-red-100 text-red-700'
                                                                        }`}>
                                                                            Decision: {d.qcDecision}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {d.returnRequestId && <p className="text-[10px] text-gray-400">Return: {d.returnRequestId}</p>}
                                                                {d.notes && <p className="text-xs text-gray-600 bg-white p-2 rounded-lg border border-gray-200 leading-relaxed">💬 {d.notes}</p>}
                                                            </div>
                                                        )}

                                                        {/* === Refund Initiated === */}
                                                        {isRefundInit && (
                                                            <div className="space-y-1.5">
                                                                <div className="flex items-center gap-2 flex-wrap text-xs">
                                                                    {d.refundAmount != null && (
                                                                        <span className="font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded">
                                                                            ₹{Number(d.refundAmount).toFixed(2)}
                                                                        </span>
                                                                    )}
                                                                    {d.refundMethod && (
                                                                        <span className="font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                                                                            via {d.refundMethod.replace(/_/g, ' ')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {d.returnRequestId && <p className="text-[10px] text-gray-400">Return: {d.returnRequestId}</p>}
                                                                {d.notes && <p className="text-xs text-gray-600 bg-white p-2 rounded-lg border border-gray-200 leading-relaxed">💬 {d.notes}</p>}
                                                            </div>
                                                        )}

                                                        {/* === Refund Completed === */}
                                                        {isRefundDone && (
                                                            <div className="space-y-1.5">
                                                                <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                                                    ✅ Refund Completed
                                                                </span>
                                                                {d.refundTransactionId && (
                                                                    <div className="flex items-center gap-1.5 text-xs">
                                                                        <span className="text-gray-500">Transaction ID:</span>
                                                                        <span className="font-mono font-medium text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">{d.refundTransactionId}</span>
                                                                    </div>
                                                                )}
                                                                {d.returnRequestId && <p className="text-[10px] text-gray-400">Return: {d.returnRequestId}</p>}
                                                                {d.notes && <p className="text-xs text-gray-600 bg-white p-2 rounded-lg border border-gray-200 leading-relaxed">💬 {d.notes}</p>}
                                                            </div>
                                                        )}

                                                        {/* === Note Added === */}
                                                        {isNote && (
                                                            <div className="space-y-1">
                                                                {d.note && <p className="text-xs text-gray-700 bg-white p-2 rounded-lg border border-amber-200 leading-relaxed">{d.note}</p>}
                                                                {d.isInternal && (
                                                                    <span className="text-[10px] text-amber-500 font-medium">🔒 Internal note — not visible to customer</span>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* === Accountability Assigned === */}
                                                        {isAccountability && (
                                                            <div className="space-y-1.5">
                                                                <div className="flex items-center gap-2 text-xs flex-wrap">
                                                                    <span className="text-gray-500">Assigned to:</span>
                                                                    <span className="font-semibold text-purple-700 bg-purple-100 px-2 py-0.5 rounded">{d.assignedTo}</span>
                                                                    {d.assignedRole && (
                                                                        <span className="font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">({d.assignedRole})</span>
                                                                    )}
                                                                </div>
                                                                {d.reason && <p className="text-xs text-gray-600 bg-white p-2 rounded-lg border border-purple-200 leading-relaxed">💬 {d.reason}</p>}
                                                            </div>
                                                        )}

                                                        {/* === Item Decision === */}
                                                        {isItemDecision && (
                                                            <div className="space-y-1.5">
                                                                {d.qcDecision && (
                                                                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                                                                        d.qcDecision === 'resale' ? 'bg-green-100 text-green-700' :
                                                                        d.qcDecision === 'refurbish' ? 'bg-amber-100 text-amber-700' :
                                                                        'bg-red-100 text-red-700'
                                                                    }`}>
                                                                        Decision: {d.qcDecision}
                                                                    </span>
                                                                )}
                                                                {d.notes && <p className="text-xs text-gray-600 bg-white p-2 rounded-lg border border-gray-200 leading-relaxed">💬 {d.notes}</p>}
                                                            </div>
                                                        )}

                                                        {/* Created By — for all events */}
                                                        {event.createdByName && (
                                                            <p className="text-[10px] text-gray-400 mt-2 pt-1.5 border-t border-gray-100">
                                                                By: {event.createdByName}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <BsGraphUp className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                    <p className="text-sm text-gray-500">No timeline events yet</p>
                                    <p className="text-xs text-gray-400 mt-1">Events will appear as the order progresses</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Courier Selection Modal (Ship Now) */}
            {showCourierModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">Select Courier Partner</h2>
                                {courierOrderInfo && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        {courierOrderInfo.pickupPincode} → {courierOrderInfo.deliveryPincode} &nbsp;|&nbsp;
                                        Weight: {courierOrderInfo.weight} Kg &nbsp;|&nbsp;
                                        {courierOrderInfo.isCod ? 'COD' : 'Prepaid'} &nbsp;|&nbsp;
                                        ₹{Number(courierOrderInfo.orderValue || 0).toFixed(2)}
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => { setShowCourierModal(false); setShippingCourierId(null); }}
                                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                            >
                                &times;
                            </button>
                        </div>

                        {/* Filter Tabs */}
                        {!courierModalLoading && !courierModalError && courierList.length > 0 && (
                            <div className="flex gap-1 px-6 pt-4 pb-2">
                                {[
                                    { key: 'all', label: 'All' },
                                    { key: 'recommended', label: 'Recommended' },
                                    { key: 'surface', label: 'Surface' },
                                    { key: 'air', label: 'Air' },
                                ].map(tab => {
                                    const isActive = courierFilter === tab.key;
                                    return (
                                        <button
                                            key={tab.key}
                                            onClick={() => setCourierFilter(tab.key)}
                                            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            {tab.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-6 pt-2">
                            {/* Customer's checkout selection banner */}
                            {customerSelectedCourier?.courier_name && !courierModalLoading && (
                                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-xs font-semibold text-blue-700 mb-0.5">📋 Customer Selected at Checkout:</p>
                                    <p className="text-sm text-blue-900">
                                        <span className="font-medium">{customerSelectedCourier.type || 'Delivery'}</span>
                                        {' — '}{customerSelectedCourier.courier_name}
                                        {customerSelectedCourier.rate != null && (
                                            <span className="font-bold ml-1">₹{Number(customerSelectedCourier.rate).toFixed(2)}</span>
                                        )}
                                    </p>
                                    <p className="text-[10px] text-blue-500 mt-0.5">
                                        Note: Live rates below may differ from checkout rates due to weight/COD/route recalculation.
                                    </p>
                                </div>
                            )}
                            {courierModalLoading && (
                                <div className="flex flex-col items-center justify-center py-16">
                                    <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                                    <p className="text-gray-500 text-sm">Fetching available couriers...</p>
                                </div>
                            )}

                            {courierModalError && !courierModalLoading && (
                                <div className="text-center py-12">
                                    <p className="text-red-500 font-medium">{courierModalError}</p>
                                    <button
                                        onClick={() => setShowCourierModal(false)}
                                        className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}

                            {!courierModalLoading && !courierModalError && courierList.length === 0 && (
                                <p className="text-center text-gray-500 py-12">No couriers available for this route.</p>
                            )}

                            {!courierModalLoading && courierList.length > 0 && (() => {
                                // Helper: parse Shiprocket date string "Apr 03, 2026" to Date
                                const parseShiprocketDate = (dateStr) => {
                                    if (!dateStr || typeof dateStr !== 'string') return null;
                                    try {
                                        const d = new Date(dateStr);
                                        if (!isNaN(d.getTime()) && d.getFullYear() > 2000) return d;
                                    } catch { /* ignore */ }
                                    return null;
                                };

                                // Helper: get Expected Pickup display from suppress_date
                                const getPickupDisplay = (courier) => {
                                    const suppressDate = parseShiprocketDate(courier.suppress_date);
                                    if (!suppressDate) return '-';
                                    const now = new Date();
                                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                    const target = new Date(suppressDate.getFullYear(), suppressDate.getMonth(), suppressDate.getDate());
                                    const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
                                    if (diffDays <= 0) return 'Today';
                                    if (diffDays === 1) return 'Tomorrow';
                                    return suppressDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                };

                                // Helper: check if courier has auto-scheduled pickup
                                const getAutoScheduledInfo = (courier) => {
                                    if (courier.suppress_text && courier.suppress_text.trim()) return courier.suppress_text.trim();
                                    try {
                                        const others = typeof courier.others === 'string' ? JSON.parse(courier.others) : courier.others;
                                        if (others?.prefetch_awb === 1 || others?.prefetch_enabled === 1) {
                                            const pickupLabel = getPickupDisplay(courier);
                                            return `Auto-Scheduled Pickup\nfor ${pickupLabel}`;
                                        }
                                    } catch { /* ignore */ }
                                    return null;
                                };

                                // Filter couriers based on active tab (is_surface is boolean from Shiprocket API)
                                const filtered = courierList.filter(c => {
                                    if (courierFilter === 'surface') return !!c.is_surface;
                                    if (courierFilter === 'air') return !c.is_surface;
                                    if (courierFilter === 'recommended') return c.courier_company_id === recommendedCourierId;
                                    return true; // 'all'
                                }).sort((a, b) => {
                                    if (a.courier_company_id === recommendedCourierId) return -1;
                                    if (b.courier_company_id === recommendedCourierId) return 1;
                                    return (a.rate || a.freight_charge || 0) - (b.rate || b.freight_charge || 0);
                                });

                                // Rating breakdown labels (matching Shiprocket API field names)
                                const ratingMetrics = [
                                    { key: 'SLA_Breach', label: 'SLA Breach' },
                                    { key: 'SLA_Adherence', label: 'SLA Adherence' },
                                    { key: 'Attempt_Speed', label: 'Attempt Speed' },
                                    { key: 'RAD_Pendency', label: 'RAD Pendency' },
                                    { key: 'NDR_Reattempt', label: 'NDR Reattempt' },
                                    { key: 'RTO w/o_Attempt', label: 'RTO w/o Attempt' },
                                ];

                                return (
                                    <>
                                        <p className="text-sm text-gray-500 mb-3">{filtered.length} of {courierList.length} Couriers Found</p>

                                        {/* Table header */}
                                        <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider pb-2 border-b mb-2 px-2">
                                            <div className="col-span-3">Courier Partner</div>
                                            <div className="col-span-1">Rating</div>
                                            <div className="col-span-2">Expected Pickup</div>
                                            <div className="col-span-2">Est. Delivery</div>
                                            <div className="col-span-1">Wt (Kg)</div>
                                            <div className="col-span-2 text-right">Charges</div>
                                            <div className="col-span-1"></div>
                                        </div>

                                        {filtered.length === 0 ? (
                                            <p className="text-center text-gray-400 py-8 text-sm">No couriers match this filter.</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {filtered.map((courier, idx) => {
                                                    const isRecommended = courier.courier_company_id === recommendedCourierId;
                                                    const isCustomerPick = customerSelectedCourier?.courier_company_id && courier.courier_company_id === customerSelectedCourier.courier_company_id;
                                                    const isShipping = shippingCourierId === courier.courier_company_id;
                                                    const courierName = courier.courier_name || 'Unknown';
                                                    const rating = courier.rating ? parseFloat(courier.rating).toFixed(1) : null;
                                                    const chargeableWeight = courier.charge_weight || courier.chargeable_weight || courier.charged_weight || courierOrderInfo?.weight || '-';
                                                    const freightCharge = Number(courier.freight_charge || 0);
                                                    const codCharges = Number(courier.cod_charges || 0);
                                                    const totalCharge = Number(courier.rate || (freightCharge + codCharges) || 0);
                                                    const rtoCharges = courier.rto_charges || 0;
                                                    const minWeight = courier.min_weight || 0.5;
                                                    // is_surface is boolean from Shiprocket API
                                                    const courierType = courier.is_surface ? 'Surface' : 'Air';

                                                    // Pickup display
                                                    const autoScheduled = getAutoScheduledInfo(courier);
                                                    const pickupDisplay = autoScheduled ? null : getPickupDisplay(courier);

                                                    // ETD display
                                                    const etdParsed = parseShiprocketDate(courier.etd);
                                                    const etdFormatted = etdParsed
                                                        ? etdParsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                        : (courier.etd || '');
                                                    const etdDays = courier.estimated_delivery_days ? `${courier.estimated_delivery_days} days` : '';

                                                    return (
                                                        <div
                                                            key={courier.courier_company_id || idx}
                                                            className={`rounded-xl border p-4 transition-all ${isCustomerPick ? 'border-blue-400 bg-blue-50/40 ring-1 ring-blue-200' : isRecommended ? 'border-green-300 bg-green-50/30' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'}`}
                                                        >
                                                            {isCustomerPick && (
                                                                <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full mb-2 uppercase tracking-wider mr-1">
                                                                    🛒 Customer Selected
                                                                </span>
                                                            )}
                                                            {isRecommended && (
                                                                <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full mb-2 uppercase tracking-wider">
                                                                    ★ Recommended
                                                                </span>
                                                            )}

                                                            <div className="grid grid-cols-12 gap-2 items-center">
                                                                {/* Courier info */}
                                                                <div className="col-span-12 sm:col-span-3">
                                                                    <p className="font-semibold text-gray-800 text-sm">{courierName}</p>
                                                                    <p className="text-[11px] text-gray-400">
                                                                        {courierType} | Min: {minWeight} Kg
                                                                        {rtoCharges ? ` | RTO: ₹${Number(rtoCharges).toFixed(0)}` : ''}
                                                                    </p>
                                                                </div>

                                                                {/* Rating with hover tooltip */}
                                                                <div className="col-span-4 sm:col-span-1 relative group">
                                                                    {rating && (
                                                                        <>
                                                                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold cursor-default ${parseFloat(rating) >= 4 ? 'bg-green-100 text-green-700' : parseFloat(rating) >= 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                                                                {rating} ★
                                                                            </span>
                                                                            {/* Rating breakdown tooltip */}
                                                                            <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block">
                                                                                <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-48">
                                                                                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Rating Breakdown</p>
                                                                                    <div className="space-y-1.5">
                                                                                        {ratingMetrics.map(m => {
                                                                                            const val = courier[m.key];
                                                                                            if (val === undefined || val === null) return null;
                                                                                            const score = Number(val);
                                                                                            return (
                                                                                                <div key={m.key} className="flex items-center justify-between">
                                                                                                    <span className="text-[11px] text-gray-600">{m.label}</span>
                                                                                                    <span className={`text-[11px] font-bold w-8 h-8 rounded-full flex items-center justify-center border-2 ${score >= 4.5 ? 'border-green-400 text-green-600' : score >= 3 ? 'border-yellow-400 text-yellow-600' : 'border-red-400 text-red-600'}`}>
                                                                                                        {score.toFixed(1)}
                                                                                                    </span>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>

                                                                {/* Pickup */}
                                                                <div className="col-span-4 sm:col-span-2 text-xs text-gray-600">
                                                                    {autoScheduled ? (
                                                                        <div className="flex items-start gap-1">
                                                                            <span className="text-blue-500 mt-0.5">⏱</span>
                                                                            <span className="text-blue-600 font-medium leading-tight" style={{ fontSize: '11px' }}>
                                                                                {autoScheduled.split('\n').map((line, i) => (
                                                                                    <span key={i}>{line}{i === 0 && <br />}</span>
                                                                                ))}
                                                                            </span>
                                                                        </div>
                                                                    ) : (
                                                                        pickupDisplay || '-'
                                                                    )}
                                                                </div>

                                                                {/* ETD */}
                                                                <div className="col-span-4 sm:col-span-2 text-xs text-gray-600">
                                                                    <span>{etdFormatted || ''}</span>
                                                                    {etdDays && <span className="text-gray-400 ml-1">({etdDays})</span>}
                                                                    {!etdFormatted && !etdDays && '-'}
                                                                </div>

                                                                {/* Chargeable weight */}
                                                                <div className="col-span-4 sm:col-span-1 text-xs text-gray-600">
                                                                    {chargeableWeight} Kg
                                                                </div>

                                                                {/* Charges — with freight + COD breakdown */}
                                                                <div className="col-span-4 sm:col-span-2 text-right">
                                                                    <p className="text-sm font-bold text-gray-800">₹{totalCharge.toFixed(2)}</p>
                                                                    {(freightCharge > 0 || codCharges > 0) && (
                                                                        <p className="text-[10px] text-gray-400 leading-tight">
                                                                            {freightCharge > 0 && <span>Freight: ₹{freightCharge.toFixed(2)}</span>}
                                                                            {freightCharge > 0 && codCharges > 0 && <span> + </span>}
                                                                            {codCharges > 0 && <span>COD: ₹{codCharges.toFixed(2)}</span>}
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                {/* Ship Now button */}
                                                                <div className="col-span-4 sm:col-span-1 text-right">
                                                                    <button
                                                                        disabled={isShipping}
                                                                        onClick={async () => {
                                                                            setShippingCourierId(courier.courier_company_id);
                                                                            try {
                                                                                const { assignShiprocketAWB, scheduleShiprocketPickup, generateShiprocketLabel } = await import("../../services/ClientService");
                                                                                toast.info(`Assigning ${courierName}...`);
                                                                                const awbRes = await assignShiprocketAWB({
                                                                                    orderId: orderDetails._id,
                                                                                    courierId: courier.courier_company_id,
                                                                                });
                                                                                if (!awbRes?.success) {
                                                                                    toast.error(awbRes?.message || "Failed to assign AWB");
                                                                                    setShippingCourierId(null);
                                                                                    return;
                                                                                }
                                                                                toast.success("AWB assigned!");

                                                                                try {
                                                                                    await scheduleShiprocketPickup({ orderId: orderDetails._id });
                                                                                    toast.success("Pickup scheduled!");
                                                                                } catch (e) {
                                                                                    console.warn("Pickup scheduling skipped:", e?.message);
                                                                                }

                                                                                try {
                                                                                    await generateShiprocketLabel({ orderId: orderDetails._id });
                                                                                    toast.success("Label generated!");
                                                                                } catch (e) {
                                                                                    console.warn("Label generation skipped:", e?.message);
                                                                                }

                                                                                setShowCourierModal(false);
                                                                                setShippingCourierId(null);
                                                                                fetchOrderDetails();
                                                                            } catch (err) {
                                                                                toast.error(err?.message || "Failed to ship");
                                                                                setShippingCourierId(null);
                                                                            }
                                                                        }}
                                                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${isShipping ? 'bg-gray-200 text-gray-500 cursor-wait' : 'bg-green-600 text-white hover:bg-green-700'}`}
                                                                    >
                                                                        {isShipping ? 'Shipping...' : 'Ship Now'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-3 border-t flex justify-end">
                            <button
                                onClick={() => { setShowCourierModal(false); setShippingCourierId(null); }}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden Invoice Template (for print/download) */}
            <div style={{
                opacity: '0',
                pointerEvents: 'none'
            }}>
                <InvoiceTemplate ref={componentRef} order={mapOrderDetailsToTemplate(orderDetails)} includeGstin={includeGstinInInvoice} />
                <BillTemplate ref={billRef} order={mapOrderDetailsToTemplate(orderDetails)} size={templatePreferences.billSize} />
            </div>
        </div >

        <ConfirmModal
            visible={showShiprocketCancelConfirm}
            message="Are you sure you want to cancel this Shiprocket shipment?"
            onConfirm={async () => {
                setShowShiprocketCancelConfirm(false);
                try {
                    const { cancelShiprocketShipment } = await import("../../services/ClientService");
                    const res = await cancelShiprocketShipment({ orderId: orderDetails._id });
                    if (res?.success) {
                        toast.success("Shiprocket shipment cancelled!");
                        fetchOrderDetails();
                    } else {
                        toast.error(res?.message || "Failed to cancel shipment");
                    }
                } catch (err) {
                    toast.error("Failed to cancel shipment");
                }
            }}
            onCancel={() => setShowShiprocketCancelConfirm(false)}
            cancelButtonText="Cancel"
            confirmButtonText="Yes, Cancel"
        />
        </>
    );
};

export default OrderOverview;